import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Plus, Trash2, Calculator, List, X, Check, Package, AlertCircle, ChevronUp, ChevronDown, Clock, Settings, ChevronDown as ChevronDownIcon, RefreshCw, GripVertical, FileText, AlertTriangle, MoveVertical, Edit2, ArrowRightLeft, Search, Zap, Eye, Loader2 } from 'lucide-react';
import ParsedOrderViewModal from './ParsedOrderViewModal';
import TransportEntryDetailModal from './TransportEntryDetailModal';
import { toast } from 'sonner';
import { calculateDistance } from '../utils/distanceCalculator';
import { clearGeocodeCache } from '../utils/distanceCalculator';
import { debounce } from 'lodash';
import api from '../services/api';
import { API } from '../App';
import { format } from 'date-fns';
import { BRANCHES } from '../data/branches';
import { getRunColor } from './transport-new/runColors';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

// Status Dropdown Component
const StatusDropdown = ({ entry, onStatusChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [note, setNote] = useState(entry.status_note || '');
  const [showNoteInput, setShowNoteInput] = useState(false);

  const getStatusIcon = () => {
    switch (entry.status) {
      case 'completed':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'collection':
        return <Package className="w-4 h-4 text-yellow-600" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <div className="w-4 h-4 rounded-full border-2 border-gray-400" />;
    }
  };

  const getRowColor = () => {
    switch (entry.status) {
      case 'collection':
        return 'bg-yellow-50';
      case 'failed':
        return 'bg-red-50';
      default:
        return 'bg-white';
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onMouseEnter={() => entry.status === 'failed' && entry.status_note && setShowNoteInput(false)}
        className="h-7 w-7 flex items-center justify-center rounded hover:bg-gray-100 transition-colors relative"
        disabled={disabled}
        title={entry.status === 'failed' && entry.status_note ? entry.status_note : entry.status || 'Set status'}
      >
        {getStatusIcon()}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-8 z-50 bg-white border-2 border-gray-200 rounded-lg shadow-xl p-2 min-w-[150px]">
          <div className="space-y-1">
            <button
              onClick={() => {
                onStatusChange('completed');
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-green-50 text-left transition-colors"
            >
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-sm">Complete</span>
            </button>
            
            <button
              onClick={() => {
                onStatusChange('collection');
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-yellow-50 text-left transition-colors"
            >
              <Package className="w-4 h-4 text-yellow-600" />
              <span className="text-sm">Collection</span>
            </button>
            
            <button
              onClick={() => {
                setShowNoteInput(true);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-red-50 text-left transition-colors"
            >
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm">Failed</span>
            </button>

            {showNoteInput && (
              <div className="pt-2 border-t">
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Failure note..."
                  className="h-7 text-xs mb-2"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    onStatusChange('failed', note);
                    setIsOpen(false);
                    setShowNoteInput(false);
                  }}
                  className="w-full h-7"
                  style={{ backgroundColor: '#ee7203', color: 'white' }}
                >
                  Save
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Component to fetch and display contribution for additional order number
const AdditionalOrderContribution = ({ orderNumber }) => {
  const [contribution, setContribution] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchContribution = async () => {
      try {
        const response = await api.get(`${API}/upload/contribution-data/${orderNumber.trim()}`);
        setContribution(response.data.contribution || 0);
      } catch (error) {
        setContribution(0);
      } finally {
        setLoading(false);
      }
    };
    
    if (orderNumber && orderNumber.trim()) {
      fetchContribution();
    }
  }, [orderNumber]);
  
  if (loading) return <span className="text-xs text-gray-400">...</span>;
  
  return (
    <span className={`text-sm font-bold ${contribution < 0 ? 'text-red-600' : 'text-green-600'}`}>
      £{contribution.toFixed(2)}
    </span>
  );
};

// Component to fetch and display sales for additional order number
const AdditionalOrderSales = ({ orderNumber }) => {
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchSales = async () => {
      try {
        const response = await api.get(`${API}/upload/sales-order-value/${orderNumber.trim()}`);
        setSales(response.data.sales_order_value || 0);
      } catch (error) {
        setSales(0);
      } finally {
        setLoading(false);
      }
    };
    
    if (orderNumber && orderNumber.trim()) {
      fetchSales();
    }
  }, [orderNumber]);
  
  if (loading) return <span className="text-xs text-gray-400">...</span>;
  
  return (
    <span className="text-sm font-semibold text-purple-600">
      £{sales.toFixed(2)}
    </span>
  );
};

// New Transport UI — makes a run's box (header + entries) a dnd-kit drop target so dragging
// an entry card anywhere onto this run (not directly over another card, e.g. into an empty
// run, or the run's padding) moves it here. Kept as its own component (not a hook called
// inline inside `renderEntriesTable`) so `useDroppable`'s hook-call count stays stable across
// renders even though the number of runs on a vehicle can change.
const RunDropZone = ({ id, data, disabled, className, style, dataTestId, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id, data, disabled });
  return (
    <div
      ref={setNodeRef}
      className={className}
      style={isOver && !disabled ? { ...style, outline: '2px dashed #a855f7', outlineOffset: '2px' } : style}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
};

// New Transport UI run-header action pills (+Drop / +Run / Full). On a laptop-width column
// (or any narrow vehicle column when several vehicles are shown side-by-side) these 3 small
// pills can overlap the Run badge/weight stats — this component measures its own available
// width via a hidden probe holding the exact same buttons, and collapses into a single
// "Make Edits" dropdown the moment the full set would overflow. Extracted as its own
// component (not inlined in `renderEntriesTable`) so its hooks have a stable call order
// regardless of how many runs a vehicle has, matching the pattern used by RunDropZone above.
const RunHeaderActions = ({ actions }) => {
  const wrapRef = useRef(null);
  const probeRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const wrap = wrapRef.current;
      const probe = probeRef.current;
      if (!wrap || !probe) return;
      setIsCompact(probe.scrollWidth > wrap.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions.length]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  if (actions.length === 0) return null;

  return (
    <div ref={wrapRef} className="relative flex items-center gap-1.5 min-w-0">
      {/* Invisible probe — same buttons, fixed off-screen (not just absolute) so it can never
          expand any ancestor's scroll area or get clipped; used purely to measure the natural
          (unwrapped) width of the full button set via its own scrollWidth. */}
      <div ref={probeRef} className="flex items-center gap-1.5 pointer-events-none" style={{ position: 'fixed', top: '-9999px', left: '-9999px', visibility: 'hidden' }} aria-hidden="true">
        {actions.map((a) => (
          <span key={`probe-${a.key}`} className={a.className}>{a.label}</span>
        ))}
      </div>
      {isCompact ? (
        <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
            className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white border-2 flex items-center gap-1 shadow-sm hover:shadow-md transition-all"
            style={{ borderColor: '#ee7203', color: '#ee7203' }}
            data-testid="run-header-make-edits"
          >
            Make Edits <ChevronDown className="w-3 h-3" />
          </button>
          {menuOpen && (
            <div
              className="absolute left-0 top-full mt-1 z-50 w-36 rounded-lg shadow-xl bg-white border border-gray-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {actions.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  data-testid={a.testId}
                  onClick={() => { a.onClick(); setMenuOpen(false); }}
                  className="w-full text-left text-xs font-semibold px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                  style={{ color: a.dotColor }}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: a.dotColor }} />
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        actions.map((a) => (
          <button key={a.key} type="button" onClick={a.onClick} className={a.className} style={a.style} data-testid={a.testId}>
            {a.label}
          </button>
        ))
      )}
    </div>
  );
};

const VehicleCard = ({ 
  vehicle, 
  allVehicles, 
  onUpdate, 
  onUpdateMultiple, 
  onDelete, 
  userRole, 
  user, 
  canEditVehicleDetails, 
  settings, 
  autoSave, 
  queueVehicleSave, 
  currentDate, 
  isViewMode, 
  isSaving, 
  onEntryAdded,
  onUnplanned,
  vehicleIndex, 
  globalMoveMode, 
  onEnterMoveMode, 
  onExecuteMove, 
  onExitMoveMode, 
  isMultiMoveMode, 
  onEnterMultiMoveMode, 
  onMultiMoveClick, 
  checkedEntries = [], 
  setCheckedEntries = () => {},
  checkedUnplannedItems = [], // Quick-access "Move": items picked directly from the Unplanned Queue, moved alongside checkedEntries
  onMoveUnplannedToRun = null,
  selectedDate,
  onCrossDateMove = null,
  onBeforeMove = null,
  onServiceIssueSaved = null,
  currentBranch = 'C30',
  autoPlannedOrderNumbers = null,
  simpleHeader = false, // New Transport UI: skip the big gradient vehicle-detail header (TransportNewUI renders its own compact driver header instead)
  cardStyle = false, // New Transport UI: coloured "Run N" pill header instead of the classic table-style run header
  renderEntry = null, // New Transport UI: render-prop — when set, each entry renders via this function (liquid-glass order card) instead of the classic dense table row; VehicleCard still owns all state/handlers, just hands them to the renderer via its 4th argument
  highlightedEntryId = null, // New Transport UI: matches entry.id / entry.order_number / entry.order_numbers[] — same convention as the classic board's search highlight
  dragSuppressRef = null, // New Transport UI: shared "a drag just happened" ref, checked by entry cards' click handlers to swallow the trailing native click after a dnd-kit drag
}) => {
  // DEBUG: Check vehicle data on render
  console.log('🚗 VehicleCard render:', {
    vehicleId: vehicle?.id,
    registration: vehicle?.vehicle_registration,
    hasEntries: vehicle?.entries !== undefined,
    entriesCount: vehicle?.entries?.length || 0
  });
  
  // Check if user can edit notes
  const canEditNotes = !user || user?.can_edit_notes || user?.role === 'super_admin';
  const [isRowEditMode, setIsRowEditMode] = useState(false);
  const [openTimePickerId, setOpenTimePickerId] = useState(null); // Track which time picker is open
  const [showCostBreakdown, setShowCostBreakdown] = useState(false); // Cost breakdown modal
  // Must match the backend's actual authorization model (see `_require_transport_edit` in
  // routes/transport_planning.py + routes/transport.py/service_issues.py): super_admin always,
  // OR any role with the `can_edit_transport` permission toggle granted in Team Management —
  // NOT a hardcoded role-name whitelist. A user whose role isn't 'manager'/'office' but who has
  // been explicitly granted can_edit_transport could previously flip the board into Edit Mode
  // (that toggle already checked this correctly) yet see zero editing controls on any entry —
  // no 3-dot menu, no Delete button, no drag — because this check alone still said "no".
  const canEdit = (userRole === 'super_admin' || ['manager', 'office'].includes(userRole) || user?.can_edit_transport === true) && !isViewMode;
  // Edit Transport board only: true if this line was placed by the automated
  // planning engine (drives the purple auto-planned gradient on the row).
  const isEntryAutoPlanned = (entry) => {
    if (!autoPlannedOrderNumbers || !entry) return false;
    if (entry.order_number && autoPlannedOrderNumbers.has(entry.order_number)) return true;
    return (entry.order_numbers || []).some((on) => autoPlannedOrderNumbers.has(on));
  };
  const [calculatingMileage, setCalculatingMileage] = useState(false);
  const [calculatingEntry, setCalculatingEntry] = useState(null); // Track which entry is calculating
  const isSavingRef = useRef(false); // Track if a save is in progress
  const isTypingRef = useRef(false); // Track if user is actively typing
  // Transfer Data feature removed - now using multi-move mode exclusively
  
  // Multi-select state is now passed from parent Dashboard (shared across all vehicles)
  // const [checkedEntries, setCheckedEntries] = useState([]); // Removed - now using props
  
  // Entry Edit Modal
  const [showEntryEditModal, setShowEntryEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingRunId, setEditingRunId] = useState(null);
  const isRecalculatingAfterEditRef = useRef(false); // Use REF for immediate effect
  const [entryModalData, setEntryModalData] = useState({
    customer_name: '',
    postcode: '',
    order_number: '',
    order_numbers: [], // Array for multiple order numbers
    time_slot: '',
    weight: 0,
    miles: 0,
    contribution: 0,
    sales_order_value: 0,
    status: '', // Add status field
    status_note: '', // Add status note field
    linked_transfers: [] // Stock transfers being collected
  });

  // Parsed order lookup state
  const [parsedOrder, setParsedOrder] = useState(null);
  const [orderSender, setOrderSender] = useState('');
  const [lookingUpOrder, setLookingUpOrder] = useState(false);
  const [showParsedOrderPanel, setShowParsedOrderPanel] = useState(false);
  // Standalone parsed-order viewer (opened by clicking a line in view mode)
  const [viewParsedOrder, setViewParsedOrder] = useState(null);
  const [viewParsedOrderEntry, setViewParsedOrderEntry] = useState(null);
  const [viewParsedOrderLoading, setViewParsedOrderLoading] = useState(false);
  
  // State for branch selection when status is "stock_transfer"
  const [showBranchSelect, setShowBranchSelect] = useState(false);

  // Collect-Transfer sub-modal state
  const [showCollectTransferModal, setShowCollectTransferModal] = useState(false);
  const [transferRefInputs, setTransferRefInputs] = useState(['']);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  // Duplicate-order detection when manually adding a NEW entry (already auto-added elsewhere)
  const [duplicateOrderInfo, setDuplicateOrderInfo] = useState(null);
  const skipDuplicateCheckRef = useRef(false);
  // Transfer Details modal (opened from Edit Entry)
  const [showTransferDetails, setShowTransferDetails] = useState(false);
  const [transferDetailsBranch, setTransferDetailsBranch] = useState(null);
  // Search-picker inside the Collect Transfer sub-modal
  const [showTransferPicker, setShowTransferPicker] = useState(false);
  const [transferPickerTargetIdx, setTransferPickerTargetIdx] = useState(0);
  const [transferPickerList, setTransferPickerList] = useState([]);
  const [transferPickerLoading, setTransferPickerLoading] = useState(false);

  // Fetch full branch details (address/postcode/phone) when the Transfer Details modal opens
  useEffect(() => {
    if (!showTransferDetails || !entryModalData.linked_transfers?.length) return;
    // Use collection_branch_id (where the stock is collected from = the selected branch on the transfer).
    // Legacy entries stored this under destination_branch_id. origin_branch_id is the REQUESTER (not the source),
    // so it is only used as a last resort.
    const first = entryModalData.linked_transfers[0];
    const branchCode = first.collection_branch_id || first.branch_id || first.destination_branch_id || first.origin_branch_id;
    if (!branchCode) return;
    (async () => {
      try {
        const res = await fetch(`${API}/branch-directory`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const list = await res.json();
          const code = String(branchCode).toLowerCase();
          const match = (list || []).find((b) =>
            String(b.id || '').toLowerCase() === code ||
            String(b.branchCode || '').toLowerCase() === code ||
            String(b.name || '').toLowerCase() === code
          );
          if (match) setTransferDetailsBranch(match);
        }
      } catch (_) { /* ignore */ }
    })();
  }, [showTransferDetails, entryModalData.linked_transfers]);

  const openTransferPicker = async (targetIdx) => {
    setTransferPickerTargetIdx(targetIdx);
    setShowTransferPicker(true);
    setTransferPickerLoading(true);
    try {
      const res = await fetch(`${API}/stock-transfers/recent?all_branches=true`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        let list = data.transfers || [];
        // If we're already in "add more from same branch" mode, filter the picker to only show
        // transfers from the same source branch as the first linked transfer on this entry.
        const pinned = entryModalData.linked_transfers?.[0];
        const pinnedBranchId = pinned?.collection_branch_id || pinned?.branch_id || pinned?.destination_branch_id;
        if (pinnedBranchId) {
          list = list.filter((t) => t.branch_id === pinnedBranchId);
        }
        // Exclude transfers already linked on this entry
        const alreadyLinkedIds = new Set((entryModalData.linked_transfers || []).map((t) => t.id));
        list = list.filter((t) => !alreadyLinkedIds.has(t.id));
        setTransferPickerList(list);
      } else {
        setTransferPickerList([]);
      }
    } catch (e) {
      setTransferPickerList([]);
    } finally {
      setTransferPickerLoading(false);
    }
  };

  // Listen for keyboard shortcut to open add entry modal
  useEffect(() => {
    const handleOpenAddEntry = (e) => {
      if (e.detail.vehicleId === vehicle.id) {
        // Open add entry modal for this vehicle
        setEntryModalData({
          customer_name: '',
          postcode: '',
          order_number: '',
          weight: '',
          time_slot: '',
          status: '',
          status_note: '',
          contribution: '',
          sales_order_value: 0,
          miles: 0
        });
        setEditingEntry(null);
        setEditingRunId(null);
        setParsedOrder(null);
        setOrderSender('');
        setShowParsedOrderPanel(false);
        setShowEntryEditModal(true);
      }
    };

    window.addEventListener('openAddEntry', handleOpenAddEntry);
    return () => window.removeEventListener('openAddEntry', handleOpenAddEntry);
  }, [vehicle.id]);
  
  // Entry Status Bar (3-dot menu functionality)
  const [openStatusBarId, setOpenStatusBarId] = useState(null); // Format: 'runId-entryId' or 'main-entryId'
  const [openNoteDropdown, setOpenNoteDropdown] = useState(null); // Format: 'runId-entryId' or 'main-entryId'
  const [tempNoteValue, setTempNoteValue] = useState(''); // Temporary note value before saving
  
  // Cancelled-section entries (New UI) still need to open the same entry detail modal
  // as any active entry — View mode shows the read-only TransportEntryDetailModal,
  // Edit mode opens the normal edit form via openEntryModalForEdit.
  const [cancelledViewEntry, setCancelledViewEntry] = useState(null); // { entry, runId } | null

  // Service Issue State
  const [showServiceIssueModal, setShowServiceIssueModal] = useState(false);
  const [selectedServiceIssueEntry, setSelectedServiceIssueEntry] = useState(null);
  const [selectedServiceIssueRunId, setSelectedServiceIssueRunId] = useState(null);
  const [serviceIssueData, setServiceIssueData] = useState({
    category: '',
    notes: '',
    id: null
  });
  const [entryServiceIssues, setEntryServiceIssues] = useState({}); // Track which entries have issues
  
  // Vehicle Edit Modal
  const [showVehicleEditModal, setShowVehicleEditModal] = useState(false);
  const [vehicleEditData, setVehicleEditData] = useState({
    vehicle_registration: '',
    vehicle_type: 'truck',
    driver_name: '',
    driver_phone: '',
    cost_per_mile: 0,
    driver_cost: 0,
    max_weight: 0
  });
  
  // Delete Confirmation Modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'entry'|'run', id, runId }
  
  // Use global move mode from Dashboard (passed as props)
  // globalMoveMode: { vehicleId, entryId, runId }
  
  // Move to Vehicle State
  // Move to Vehicle feature removed - now using multi-move mode exclusively
  
  // Multi-selection state for bulk move/copy
  const [showCopyMoveModal, setShowCopyMoveModal] = useState(false);
  const [pendingMoveTarget, setPendingMoveTarget] = useState(null); // { targetEntryId, targetRunId }

  // Check if vehicle has any entries (main or runs)
  const hasAnyEntries = () => {
    const mainEntries = vehicle.entries?.length || 0;
    const runEntries = (vehicle.runs || []).reduce((total, run) => total + (run.entries?.length || 0), 0);
    return mainEntries + runEntries > 0;
  };

  // Debounced auto-save function - only save if there are entries
  const debouncedAutoSave = useCallback(
    debounce(() => {
      // Don't save if already saving, user is typing, OR recalculating after edit
      if (isSavingRef.current || isTypingRef.current || isRecalculatingAfterEditRef.current) {
        console.log('⏸️ Auto-save blocked:', { saving: isSavingRef.current, typing: isTypingRef.current, recalculating: isRecalculatingAfterEditRef.current });
        return;
      }
      
      if (autoSave && hasAnyEntries()) {
        isSavingRef.current = true;
        Promise.resolve(autoSave()).finally(() => {
          // Add a small delay before allowing next save
          setTimeout(() => {
            isSavingRef.current = false;
          }, 200);
        });
      }
    }, 500), // 500ms debounce - reduced rapid-fire saves to prevent conflicts
    [autoSave]
  );
  
  // CRITICAL FIX: Cancel pending debounced saves when component unmounts or vehicle changes
  // This prevents cross-date data contamination when user navigates between dates
  useEffect(() => {
    return () => {
      console.log(`🧹 VehicleCard cleanup: Cancelling pending auto-saves for ${vehicle.vehicle_registration}`);
      debouncedAutoSave.cancel();
    };
  }, [debouncedAutoSave, vehicle.vehicle_registration]);
  
  // Load service issues function - moved outside useEffect so it can be called anywhere
  const loadServiceIssues = async () => {
    try {
      const response = await api.get(`${API}/service-issues`);
      const issues = response.data.service_issues || [];
      
      // Create a map of entry_id -> service issue
      const issuesMap = {};
      issues.forEach(issue => {
        issuesMap[issue.entry_id] = issue;
      });
      
      setEntryServiceIssues(issuesMap);
    } catch (error) {
      console.error('Error loading service issues:', error);
    }
  };
  
  // Load service issues for vehicle entries on mount
  useEffect(() => {
    loadServiceIssues();
  }, [vehicle]);
  
  // Track when user starts and stops typing
  const handleInputFocus = () => {
    isTypingRef.current = true;
    // Cancel any pending auto-save when user starts typing
    debouncedAutoSave.cancel();
  };
  
  const handleInputBlur = () => {
    setTimeout(() => {
      isTypingRef.current = false;
      // Removed auto-save - using explicit saves only
    }, 150);
  };

  const updateField = (field, value) => {
    onUpdate(vehicle.id, { ...vehicle, [field]: value });
    // Don't auto-save immediately - will save on blur
  };

  // The newest copy of this vehicle. Saving an entry waits on network lookups first, so the `vehicle` prop captured when
  // Save was clicked can be out of date by the time the entry is added.
  const latestVehicleRef = useRef(vehicle);
  latestVehicleRef.current = vehicle;

  const addEntry = (newEntryData = null, runId = null) => {
    const newEntry = newEntryData || {
      id: `entry_${Date.now()}`,
      customer_name: '',
      postcode: '',
      order_number: '',
      weight: 0,
      miles: 0,
      status: 'pending',
      status_note: '',
      time_slot: '',
      notes: '',
      contribution: 0,
      sales_order_value: 0,
    };

    // Ensure entry has an ID
    if (!newEntry.id) {
      newEntry.id = `entry_${Date.now()}`;
    }

    let updatedVehicle;
    if (runId) {
      // Add entry to specific run
      const updatedRuns = vehicle.runs.map(run => {
        if (run.id === runId) {
          return { ...run, entries: [...run.entries, newEntry] };
        }
        return run;
      });
      updatedVehicle = { ...vehicle, runs: updatedRuns };
      onUpdate(vehicle.id, updatedVehicle);
    } else {
      // Add entry to main entries
      const updatedEntries = [...vehicle.entries, newEntry];
      updatedVehicle = { ...vehicle, entries: updatedEntries };
      onUpdate(vehicle.id, updatedVehicle);
    }
    // CRITICAL: Queue save immediately after adding entry
    queueVehicleSave(vehicle.id, updatedVehicle);
  };

  const addRun = () => {
    // Count existing runs
    const currentRuns = vehicle.runs ? vehicle.runs.length : 0;
    
    if (currentRuns >= 3) { // 0-indexed, so 3 means 4 total (including main)
      toast.error('Maximum 4 runs per vehicle reached');
      return;
    }

    const newRun = {
      id: `run_${Date.now()}`,
      run_number: currentRuns + 2, // +2 because run 1 is the main entries
      entries: [],
      total_miles: 0,
      total_weight: 0,
    };

    const updatedRuns = vehicle.runs ? [...vehicle.runs, newRun] : [newRun];
    const updatedVehicle = { ...vehicle, runs: updatedRuns };
    
    // Update local state first
    onUpdate(vehicle.id, updatedVehicle);
    
    // Save the actual updated vehicle object directly via the save queue, NOT `autoSave()` -
    // `autoSave` is a prop closure (`() => saveVehicle(vehicle.id)`) that reads the PARENT's
    // `vehicles` state, which hasn't re-rendered with the new run yet at this point in the same
    // tick as `onUpdate()` above. Calling it here would silently persist the vehicle WITHOUT the
    // just-added run (stale-closure bug), so the new run never actually reaches Mongo even though
    // the UI shows it and a false "saved" toast - use the freshly-computed `updatedVehicle` object
    // directly instead, same pattern as every other mutation in this file.
    queueVehicleSave(vehicle.id, updatedVehicle);
    toast.success('Run added');
  };

  const updateEntry = async (entryId, field, value, runId = null) => {
    // If value is an object, it means we're updating multiple fields at once (from modal)
    const updates = typeof value === 'object' && value !== null && field === null
      ? value
      : { [field]: value };

    // Manually marking a drop complete (3-dot menu / status dropdown) with no existing
    // GPS-confirmed position — backfill actual_lat/actual_lng/completed_at from the
    // nearest point in THIS vehicle's own breadcrumb trail to this drop's postcode, so
    // the map's breadcrumb colouring (which only ever reads that GPS-confirmed data —
    // see services/samsara_geofence.py's docstring) reflects reality even when the
    // automated geofence detector missed this specific stop. Best-effort only: a manual
    // click for a vehicle/day with no matching Samsara data nearby simply finds nothing
    // and the entry keeps its plain "manually completed" state (never invents a location).
    if (updates.status === 'complete' && vehicle?.id && currentDate) {
      const entriesHere = runId ? (vehicle.runs?.find((r) => r.id === runId)?.entries || []) : (vehicle.entries || []);
      const targetEntry = entriesHere.find((e) => e.id === entryId);
      if (targetEntry && targetEntry.actual_lat == null && targetEntry.postcode) {
        try {
          const res = await api.get(`${API}/transport/samsara/nearest-breadcrumb`, {
            params: { vehicle_id: vehicle.registry_vehicle_id || vehicle.id, registration: vehicle.vehicle_registration, date: currentDate, postcode: targetEntry.postcode },
          });
          if (res.data?.point) {
            updates.actual_lat = res.data.point.lat;
            updates.actual_lng = res.data.point.lng;
            updates.completed_at = res.data.point.time || new Date().toISOString();
          }
        } catch (e) { /* best-effort only — leave as a plain manual completion */ }
      }
      if (targetEntry && !targetEntry.completed_at && !updates.completed_at) {
        updates.completed_at = new Date().toISOString();
      }
    }

    console.log('🔍 updateEntry called:', {
      entryId,
      field,
      value,
      runId,
      vehicleHasEntries: vehicle.entries !== undefined,
      vehicleEntriesCount: vehicle.entries?.length || 0
    });

    let updatedVehicle;
    if (runId) {
      // Update entry in specific run
      const updatedRuns = vehicle.runs.map(run => {
        if (run.id === runId) {
          const updatedEntries = run.entries.map(entry =>
            entry.id === entryId ? { ...entry, ...updates } : entry
          );
          return { ...run, entries: updatedEntries };
        }
        return run;
      });
      updatedVehicle = { ...vehicle, runs: updatedRuns };
      onUpdate(vehicle.id, updatedVehicle);
    } else {
      // Update entry in main entries
      // CRITICAL: Ensure vehicle.entries exists
      if (!vehicle.entries || vehicle.entries.length === 0) {
        console.error('❌ ERROR: vehicle.entries is empty or undefined!', {
          vehicleId: vehicle.id,
          registration: vehicle.vehicle_registration,
          hasEntries: vehicle.entries !== undefined,
          entriesCount: vehicle.entries?.length || 0
        });
        toast.error('ERROR: Vehicle has no entries to update!');
        return vehicle; // Return unchanged vehicle
      }
      
      const updatedEntries = vehicle.entries.map((entry) =>
        entry.id === entryId ? { ...entry, ...updates } : entry
      );
      updatedVehicle = { ...vehicle, entries: updatedEntries };
      console.log('🔍 updatedVehicle created:', {
        hasEntries: updatedVehicle.entries !== undefined,
        entriesCount: updatedVehicle.entries?.length || 0,
        firstEntryStatus: updatedVehicle.entries?.[0]?.status,
        updatedEntryId: entryId,
        updatedField: field,
        updatedValue: value
      });
      onUpdate(vehicle.id, updatedVehicle);
    }
    
    // CRITICAL FIX: Return updated vehicle so callers can save it immediately
    console.log('🔍 updateEntry returning vehicle with entries:', updatedVehicle.entries?.length || 0);
    return updatedVehicle;
  };

  const autoSortByTime = (runId = null) => {
    // Sort entries by time_slot
    const sortEntries = (entries) => {
      return [...entries].sort((a, b) => {
        if (!a.time_slot && !b.time_slot) return 0;
        if (!a.time_slot) return 1;
        if (!b.time_slot) return -1;
        return a.time_slot.localeCompare(b.time_slot);
      });
    };

    if (runId) {
      // Sort entries in specific run
      const updatedRuns = vehicle.runs.map(run => {
        if (run.id === runId) {
          return { ...run, entries: sortEntries(run.entries) };
        }
        return run;
      });
      onUpdate(vehicle.id, { ...vehicle, runs: updatedRuns });
    } else {
      // Sort main entries
      const sortedEntries = sortEntries(vehicle.entries);
      onUpdate(vehicle.id, { ...vehicle, entries: sortedEntries });
    }
  };


  const handlePostcodeBlur = async (entryId, postcode, runId = null, entryIndex) => {
    // Calculate sequential miles when user finishes typing postcode
    if (postcode && postcode.trim()) {
      try {
        setCalculatingEntry(entryId); // Show calculating indicator
        
        // CRITICAL FIX: Wait for calculation to complete and get updated vehicle
        const updatedVehicle = await calculateSequentialMileage(runId, entryIndex);
        
        // CRITICAL FIX: Save immediately with recalculated mileage
        if (updatedVehicle) {
          queueVehicleSave(vehicle.id, updatedVehicle);
          console.log('✅ Postcode edited - mileage recalculated and saved');
        }
      } catch (error) {
        console.error('Error in handlePostcodeBlur:', error);
        toast.error(`Failed to calculate mileage for ${postcode}. APIs may be unavailable. Please enter miles manually (blue field is editable).`, {
          duration: 5000,
        });
      } finally {
        setCalculatingEntry(null); // Hide calculating indicator
      }
    }
  };

  // Open entry edit modal for new entry
  const openEntryModalForNew = (runId = null) => {
    setEditingEntry(null);
    setEditingRunId(runId);
    setEntryModalData({
      customer_name: '',
      postcode: '',
      order_number: '',
      time_slot: '',
      weight: 0,
      miles: 0,
      contribution: 0,
      sales_order_value: 0
    });
    setShowEntryEditModal(true);
  };

  // Open vehicle edit modal
  const openVehicleEditModal = () => {
    setVehicleEditData({
      vehicle_registration: vehicle.vehicle_registration || '',
      vehicle_type: vehicle.vehicle_type || 'truck',
      driver_name: vehicle.driver_name || '',
      driver_phone: vehicle.driver_phone || '',
      cost_per_mile: vehicle.cost_per_mile || 0,
      driver_cost: vehicle.driver_cost || 0,
      max_weight: vehicle.max_weight || 0
    });
    setShowVehicleEditModal(true);
  };
  
  // Save vehicle details from modal
  const saveVehicleFromModal = () => {
    const updatedVehicle = {
      ...vehicle,
      ...vehicleEditData
    };
    onUpdate(vehicle.id, updatedVehicle);
    setShowVehicleEditModal(false);
    toast.success('Vehicle details updated');
    
    // CRITICAL FIX: Save immediately with updated data (no setTimeout delay)
    queueVehicleSave(vehicle.id, updatedVehicle);
  };

  // Open entry edit modal for existing entry
  const openEntryModalForEdit = async (entry, runId = null) => {
    setEditingEntry(entry);
    setEditingRunId(runId);
    setOrderSender('');
    setEntryModalData({
      customer_name: entry.customer_name || '',
      postcode: entry.postcode || '',
      order_number: entry.order_number || '',
      order_numbers: entry.order_numbers || [],
      time_slot: entry.time_slot || '',
      weight: entry.weight || 0,
      miles: entry.miles || 0,
      contribution: entry.contribution || 0,
      sales_order_value: entry.sales_order_value || 0,
      status: entry.status || '',
      status_note: entry.status_note || '',
      linked_transfers: entry.linked_transfers || [],
    });

    // Use embedded snapshot first — survives automation email deletion
    if (entry.embedded_order) {
      setParsedOrder(entry.embedded_order);
      setShowParsedOrderPanel(true);
      // Resolve sender from embedded order's from_email
      const fromEmail = entry.embedded_order.from_email;
      if (fromEmail) {
        api.get(`${API}/automations/resolve-sender?email=${encodeURIComponent(fromEmail)}`)
          .then(r => setOrderSender(r.data.nickname || ''))
          .catch(() => {});
      }
    } else if (entry.status === 'stock_transfer') {
      // Stock transfer entries store the transfer reference in `order_number`, NOT a
      // sales-order number — it can coincidentally collide with an unrelated sales
      // order's number in automation_emails, so never attempt a parsed-order lookup
      // here. The dedicated "Transfer Details" button shows the correct info instead.
      setParsedOrder(null);
      setShowParsedOrderPanel(false);
    } else {
      setParsedOrder(null);
      setShowParsedOrderPanel(false);
      // Fall back to live lookup only if no snapshot exists
      const orderNum = (entry.order_number || '').trim();
      if (orderNum) {
        try {
          const res = await api.get(`${API}/automations/orders/${orderNum}`);
          setParsedOrder(res.data);
          setShowParsedOrderPanel(true);
          // Resolve sender from live order
          const fromEmail = res.data.from_email;
          if (fromEmail) {
            api.get(`${API}/automations/resolve-sender?email=${encodeURIComponent(fromEmail)}`)
              .then(r => setOrderSender(r.data.nickname || ''))
              .catch(() => {});
          }
        } catch { /* no live match — panel stays hidden */ }
      }
    }

    setShowEntryEditModal(true);
  };

  // Lookup parsed order and auto-fill fields
  // Cancelled Ticket check — Stock Management flags an order number cancelled
  // (routes/cancelled_tickets.py); warn staff the moment that number is entered
  // on a today/future-dated entry (a past/already-delivered date can't be
  // stopped, so skip the check there).
  const checkCancelledTicket = async (orderNum) => {
    if (!orderNum || !currentDate) return;
    const todayStr = new Date().toISOString().slice(0, 10);
    if (currentDate < todayStr) return;
    try {
      const res = await api.get(`${API}/cancelled-tickets/check/${encodeURIComponent(orderNum)}`);
      if (res.data?.cancelled) {
        toast.error(`⚠ Order #${orderNum} is CANCELLED — do not deliver`, { duration: 8000 });
      }
    } catch { /* best-effort only */ }
  };

  const handleOrderLookup = async () => {
    if (entryModalData.status === 'stock_transfer') {
      // The order_number field on a transfer entry holds the transfer reference, not a
      // sales order — don't look it up as one (it can coincidentally match an unrelated
      // sales order's number). Show the real Transfer Details instead.
      if ((entryModalData.linked_transfers?.length || 0) > 0) {
        setShowTransferDetails(true);
      } else {
        toast.info('This is a stock transfer entry — use "Collect Transfer" to link a transfer');
      }
      return;
    }
    const orderNum = (entryModalData.order_number || '').trim();
    if (!orderNum) { toast.error('Enter an order number first'); return; }
    checkCancelledTicket(orderNum);
    setLookingUpOrder(true);
    try {
      const res = await api.get(`${API}/automations/orders/${orderNum}`);
      const order = res.data;
      setParsedOrder(order);
      setShowParsedOrderPanel(true);

      // Resolve sender nickname from from_email
      if (order.from_email) {
        try {
          const sRes = await api.get(`${API}/automations/resolve-sender?email=${encodeURIComponent(order.from_email)}`);
          setOrderSender(sRes.data.nickname || '');
        } catch { setOrderSender(''); }
      }

      const updates = {};
      if (order.delivery_postcode) updates.postcode = order.delivery_postcode;
      if (order.invoice_customer_name || order.customer_name) {
        updates.customer_name = (order.invoice_customer_name || order.customer_name).toUpperCase();
      }

      // Calc weight
      try {
        const wRes = await api.get(`${API}/automations/weight/${orderNum}`);
        if (wRes.data.total_weight > 0) updates.weight = wRes.data.total_weight;
      } catch { /* no weight data */ }

      if (Object.keys(updates).length > 0) {
        setEntryModalData(prev => ({ ...prev, ...updates }));
        const filled = [
          updates.postcode && 'postcode',
          updates.customer_name && 'customer name',
          updates.weight && 'weight',
        ].filter(Boolean).join(', ');
        toast.success(`Auto-filled: ${filled}`);
      } else {
        toast.info('Order found — no auto-fill data available yet');
      }
    } catch {
      toast.error('Order not found — check the order number matches an uploaded PDF');
      setParsedOrder(null);
      setShowParsedOrderPanel(false);
      setOrderSender('');
    } finally {
      setLookingUpOrder(false);
    }
  };

  // Save entry from modal
  const saveEntryFromModal = async () => {
    if (!entryModalData.postcode || !entryModalData.postcode.trim()) {
      toast.error('Postcode is required');
      return;
    }

    try {
      if (editingEntry) {
        // If the user added/changed an order number so it now matches something
        // ELSE already on the board or in an Unplanned Queue, flag it as a
        // duplicate before saving — same "Move Here / Cancel" dialog used when
        // adding a brand-new entry. Numbers that were already on this entry
        // before this edit are skipped (they're not new — nothing to check).
        const editedOrderNumbers = [];
        if (entryModalData.order_number && entryModalData.order_number.trim()) {
          editedOrderNumbers.push(entryModalData.order_number.trim());
        }
        (entryModalData.order_numbers || []).forEach(n => {
          if (n && n.trim()) editedOrderNumbers.push(n.trim());
        });
        const ownOrderNumbersBeforeEdit = new Set(
          [editingEntry.order_number, ...(editingEntry.order_numbers || [])]
            .filter(n => n && n.trim())
            .map(n => n.trim())
        );
        const newlyAddedOrderNumbers = editedOrderNumbers.filter(n => !ownOrderNumbersBeforeEdit.has(n));

        if (!skipDuplicateCheckRef.current && newlyAddedOrderNumbers.length > 0) {
          for (const orderNum of newlyAddedOrderNumbers) {
            try {
              const dupRes = await api.get(`${API}/transport-planning/check-duplicate/${encodeURIComponent(orderNum)}`);
              if (dupRes.data?.exists) {
                setDuplicateOrderInfo({ ...dupRes.data, order_number: orderNum });
                return; // abort — wait for the user's Move Here / Cancel decision
              }
            } catch {
              // If the check itself fails, don't block the user — fail open
            }
          }
        }
        skipDuplicateCheckRef.current = false;

        // Update existing entry with fresh vehicle data
        // Prepare updated entry data with filtered order_numbers
        const updatedEntryData = {
          ...entryModalData,
          order_numbers: (entryModalData.order_numbers || []).filter(n => n && n.trim()),
          embedded_order: parsedOrder || editingEntry.embedded_order || null,  // keep existing snapshot if no new one
        };
        console.log('📝 Updating entry with order_numbers:', updatedEntryData.order_numbers);
        console.log('📝 Full entry data being saved:', updatedEntryData);
        
        let updatedVehicle;
        
        if (editingRunId) {
          // Update entry in specific run
          const updatedRuns = vehicle.runs.map(run => {
            if (run.id === editingRunId) {
              const updatedEntries = run.entries.map(entry =>
                entry.id === editingEntry.id ? { ...entry, ...updatedEntryData } : entry
              );
              return { ...run, entries: updatedEntries };
            }
            return run;
          });
          updatedVehicle = { ...vehicle, runs: updatedRuns };
        } else {
          // Update entry in main entries
          const updatedEntries = vehicle.entries.map((entry) =>
            entry.id === editingEntry.id ? { ...entry, ...updatedEntryData } : entry
          );
          updatedVehicle = { ...vehicle, entries: updatedEntries };
        }
        
        // CRITICAL FIX: Check if postcode was changed - if so, save immediately then recalculate mileage
        const postcodeChanged = editingEntry.postcode !== updatedEntryData.postcode;
        
        // Update state immediately
        onUpdate(vehicle.id, updatedVehicle);
        
        // CRITICAL FIX: Save IMMEDIATELY and WAIT for confirmation before closing modal
        console.log(`💾 IMMEDIATE SAVE: Entry edited, queueing save...`);
        console.log(`💾 Vehicle data being queued:`, {
          id: updatedVehicle.id,
          entriesCount: updatedVehicle.entries?.length || 0,
          runsCount: updatedVehicle.runs?.length || 0,
          firstEntryOrderNumbers: updatedVehicle.entries?.[0]?.order_numbers
        });
        queueVehicleSave(vehicle.id, updatedVehicle);
        
        // Close modal and reset state AFTER queueing save
        setShowEntryEditModal(false);
        setEditingEntry(null);
        setEditingRunId(null);
        setParsedOrder(null);
        setOrderSender('');
        setEntryModalData({
          customer_name: '',
          postcode: '',
          order_number: '',
          order_numbers: [],
          time_slot: '',
          weight: 0,
          miles: 0,
          contribution: 0,
          sales_order_value: 0,
          status: 'pending',
          status_note: '',
        });
        
        // Silent save - no toast, just queue it
        
        if (postcodeChanged) {
          // Block other auto-saves during recalculation
          isRecalculatingAfterEditRef.current = true;
          
          // Recalculate mileage in background
          setTimeout(async () => {
            try {
              console.log('🔵 Background: Recalculating mileage for edited entry...');
              
              // CRITICAL: Pass updatedVehicle so it uses the NEW postcode, not old prop
              const vehicleWithRecalculatedMileage = await calculateSequentialMileage(editingRunId, null, updatedVehicle);
              
              // Update state with new mileage
              onUpdate(vehicle.id, vehicleWithRecalculatedMileage);
              
              console.log(`✅ Mileage recalculated, queueing update...`);
              
              // Queue another save with calculated mileage
              queueVehicleSave(vehicle.id, vehicleWithRecalculatedMileage);
              
              // Re-enable auto-saves
              isRecalculatingAfterEditRef.current = false;
              console.log('✅ Mileage update saved, auto-save re-enabled');
              
            } catch (error) {
              console.error('Error recalculating mileage:', error);
              isRecalculatingAfterEditRef.current = false;
              toast.warning('Entry saved but mileage calculation failed. You can recalculate later.');
            }
          }, 100);
        }
        
        return;
      }

      // Collect all order numbers (main + additional)
      const allOrderNumbers = [];
      if (entryModalData.order_number && entryModalData.order_number.trim()) {
        allOrderNumbers.push(entryModalData.order_number.trim());
      }
      if (entryModalData.order_numbers && entryModalData.order_numbers.length > 0) {
        entryModalData.order_numbers.forEach(orderNum => {
          if (orderNum && orderNum.trim()) {
            allOrderNumbers.push(orderNum.trim());
          }
        });
      }

      // If this order is ALREADY auto-added (or manually placed) somewhere else on
      // Transport, ask the user before creating a duplicate entry here.
      if (!skipDuplicateCheckRef.current && allOrderNumbers.length > 0) {
        for (const orderNum of allOrderNumbers) {
          try {
            const dupRes = await api.get(`${API}/transport-planning/check-duplicate/${encodeURIComponent(orderNum)}`);
            if (dupRes.data?.exists) {
              setDuplicateOrderInfo({ ...dupRes.data, order_number: orderNum });
              return; // abort — wait for the user's Move Here / Cancel decision
            }
          } catch {
            // If the check itself fails, don't block the user — fail open
          }
        }
      }
      skipDuplicateCheckRef.current = false;

      // Fetch contribution and sales order value for ALL order numbers (main + additional)
      let fetchedContribution = entryModalData.contribution || 0;
      let fetchedSalesOrderValue = entryModalData.sales_order_value || 0;
      
      // Fetch and sum data for all order numbers
      if (allOrderNumbers.length > 0) {
        console.log(`🔍 Looking up ${allOrderNumbers.length} order number(s): ${allOrderNumbers.join(', ')}`);
        
        let totalContribution = 0;
        let totalSalesOrderValue = 0;
        let foundCount = 0;
        
        for (const orderNum of allOrderNumbers) {
          try {
            // Fetch contribution
            const contributionResponse = await api.get(`${API}/upload/contribution-data/${orderNum}`);
            if (contributionResponse.data.contribution !== null) {
              totalContribution += contributionResponse.data.contribution;
              console.log(`✅ Found contribution for ${orderNum}: ${contributionResponse.data.contribution}`);
              foundCount++;
            }
            
            // Fetch sales order value
            const salesOrderResponse = await api.get(`${API}/upload/sales-order-value/${orderNum}`);
            if (salesOrderResponse.data.sales_order_value !== null) {
              totalSalesOrderValue += salesOrderResponse.data.sales_order_value;
              console.log(`✅ Found sales order value for ${orderNum}: ${salesOrderResponse.data.sales_order_value}`);
            }
          } catch (error) {
            console.log(`ℹ️ No data found for order number ${orderNum}`);
          }
        }
        
        // Use summed values
        fetchedContribution = totalContribution;
        fetchedSalesOrderValue = totalSalesOrderValue;
        
        if (foundCount > 0) {
          console.log(`✅ Total from ${foundCount} order(s): Contribution £${totalContribution.toFixed(2)}, Sales £${totalSalesOrderValue.toFixed(2)}`);
          toast.success(`Found data for ${foundCount}/${allOrderNumbers.length} order(s)`);
        }
      }

      // Add new entry - ensure it has all required fields including fetched data
      const filteredOrderNumbers = (entryModalData.order_numbers || []).filter(n => n && n.trim());
      console.log('💾 Saving new entry with order_numbers:', filteredOrderNumbers);
      
      const newEntryComplete = {
        id: `entry_${Date.now()}`,
        customer_name: (entryModalData.customer_name || '').toUpperCase(),
        postcode: entryModalData.postcode || '',
        order_number: entryModalData.order_number || '',
        order_numbers: filteredOrderNumbers,
        weight: entryModalData.weight || 0,
        miles: 0, // Will be calculated
        status: entryModalData.status || 'pending',
        status_note: entryModalData.status_note || '',
        time_slot: entryModalData.time_slot || '',
        notes: '',
        contribution: fetchedContribution,
        sales_order_value: fetchedSalesOrderValue,
        linked_transfers: entryModalData.linked_transfers || [],
        embedded_order: parsedOrder || null,   // snapshot — survives automation email deletion
      };
      
      // Build the updated vehicle object with the new entry — on the NEWEST copy of the vehicle (see latestVehicleRef).
      const latestVehicle = latestVehicleRef.current || vehicle;
      let updatedVehicle;
      if (editingRunId) {
        // Add entry to specific run
        const updatedRuns = latestVehicle.runs.map(run => {
          if (run.id === editingRunId) {
            return { ...run, entries: [...run.entries, newEntryComplete] };
          }
          return run;
        });
        updatedVehicle = { ...latestVehicle, runs: updatedRuns };
      } else {
        // Add entry to main entries
        const updatedEntries = [...latestVehicle.entries, newEntryComplete];
        updatedVehicle = { ...latestVehicle, entries: updatedEntries };
      }

      // Update parent state
      onUpdate(vehicle.id, updatedVehicle);

      // Close modal and reset data
      setShowEntryEditModal(false);
      setEditingEntry(null);
      setEditingRunId(null);
      setEntryModalData({
        customer_name: '',
        postcode: '',
        order_number: '',
        order_numbers: [],
        time_slot: '',
        weight: 0,
        miles: 0,
        contribution: 0,
        sales_order_value: 0,
        status: 'pending',
        status_note: '',
        linked_transfers: [],
      });
      
      // Notify parent that entry was added to this vehicle
      if (onEntryAdded) {
        onEntryAdded();
      }
      
      // Save immediately - silent, no toast
      console.log('💾 Queuing new entry save');
      queueVehicleSave(updatedVehicle.id, updatedVehicle);
      
      // Calculate mileage in background and update if needed
      setTimeout(async () => {
        try {
          console.log('🔵 Background: Starting mileage calculation for new entry...');
          
          // Pass the updated vehicle data explicitly to calculateSequentialMileage
          const vehicleWithMileage = await calculateSequentialMileage(editingRunId, null, updatedVehicle);
          
          console.log('✅ Mileage calculated, queueing update...');
          
          // Queue another save with calculated mileage
          queueVehicleSave(updatedVehicle.id, vehicleWithMileage);
          console.log('✅ Mileage update queued for save');
          
        } catch (error) {
          console.error('❌ Error in background mileage calculation:', error);
          toast.warning('Entry saved but mileage calculation failed. You can recalculate later.');
        }
      }, 100); // Background calculation
      
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Failed to save entry');
    }
  };

  const confirmMoveDuplicateHere = async () => {
    if (!duplicateOrderInfo) return;
    try {
      const res = await api.post(`${API}/transport-planning/resolve-duplicate/${encodeURIComponent(duplicateOrderInfo.order_number)}`);
      const removal = res.data || {};
      // The backend has already deleted the entry from its original run in the DB.
      // If that original vehicle is also part of THIS date's board (i.e. it's sitting
      // right here in `allVehicles`), strip it out of our in-memory copy too — otherwise
      // the old entry stays visibly stuck on its original run until a full page refresh.
      if (removal.removed_from === 'board' && removal.date === currentDate && removal.vehicle_id) {
        const sourceVehicle = (allVehicles || []).find((v) => v.id === removal.vehicle_id);
        if (sourceVehicle) {
          const stripEntry = (entries) => (entries || []).filter((e) => e.id !== removal.entry_id);
          const updatedSourceVehicle = removal.run_choice === 'main'
            ? { ...sourceVehicle, entries: stripEntry(sourceVehicle.entries) }
            : { ...sourceVehicle, runs: (sourceVehicle.runs || []).map((r) => (r.id === removal.run_choice ? { ...r, entries: stripEntry(r.entries) } : r)) };
          onUpdateMultiple([{ id: updatedSourceVehicle.id, data: updatedSourceVehicle }]);
        }
      }
      setDuplicateOrderInfo(null);
      skipDuplicateCheckRef.current = true;
      await saveEntryFromModal();
    } catch {
      toast.error('Failed to move the order — please try again');
    }
  };

  const cancelDuplicateOrder = () => {
    setDuplicateOrderInfo(null);
    // Per spec: leave the auto-added order exactly where it is — abort adding entirely.
    setShowEntryEditModal(false);
    setEditingEntry(null);
    setEditingRunId(null);
  };

  // Duplicate-order modal shows dates as DD/MM/YY (e.g. "15/02/26"), not the raw
  // "yyyy-MM-dd" the backend stores dates as.
  const formatDupDate = (isoDate) => {
    if (!isoDate) return isoDate;
    try { return format(new Date(`${isoDate}T00:00:00`), 'dd/MM/yy'); } catch { return isoDate; }
  };

  const handlePostcodeBlur_OLD = async (entryId, postcode, runId = null, entryIndex) => {
    // Calculate sequential miles when user finishes typing postcode
    if (postcode && postcode.trim()) {
      try {
        setCalculatingEntry(entryId); // Show calculating indicator
        await calculateSequentialMileage(runId, entryIndex);
      } catch (error) {
        console.error('Error in handlePostcodeBlur:', error);
        toast.error(`Failed to calculate mileage for ${postcode}. APIs may be unavailable. Please enter miles manually (blue field is editable).`, {
          duration: 5000,
        });
      } finally {
        setCalculatingEntry(null); // Hide calculating indicator
      }
    }
    // State updated - no auto-save needed, explicit saves handle persistence
  };

  const handleOrderNumberBlur = async (entryId, orderNumber, runId = null, isSubEntry = false) => {
    // Auto-fetch contribution and sales order value when order number is entered
    if (orderNumber && orderNumber.trim()) {
      try {
        // Fetch contribution
        const contributionResponse = await api.get(`${API}/upload/contribution-data/${orderNumber.trim()}`);
        
        // Fetch sales order value
        const salesOrderResponse = await api.get(`${API}/upload/sales-order-value/${orderNumber.trim()}`);
        
        const contribution = contributionResponse.data.contribution;
        const salesOrderValue = salesOrderResponse.data.sales_order_value;
        
        if (contribution !== null || salesOrderValue !== null) {
          if (isSubEntry) {
            // Update sub-entry
            // Find parent entry and update sub-entry within it
            if (runId) {
              const updatedRuns = vehicle.runs.map(run => {
                if (run.id === runId) {
                  return {
                    ...run,
                    entries: run.entries.map(entry => ({
                      ...entry,
                      sub_entries: (entry.sub_entries || []).map(sub =>
                        sub.id === entryId ? {
                          ...sub,
                          contribution: contribution !== null ? contribution : sub.contribution,
                          sales_order_value: salesOrderValue !== null ? salesOrderValue : sub.sales_order_value
                        } : sub
                      )
                    }))
                  };
                }
                return run;
              });
              onUpdate(vehicle.id, { ...vehicle, runs: updatedRuns });
            } else {
              const updatedEntries = vehicle.entries.map(entry => ({
                ...entry,
                sub_entries: (entry.sub_entries || []).map(sub =>
                  sub.id === entryId ? {
                    ...sub,
                    contribution: contribution !== null ? contribution : sub.contribution,
                    sales_order_value: salesOrderValue !== null ? salesOrderValue : sub.sales_order_value
                  } : sub
                )
              }));
              onUpdate(vehicle.id, { ...vehicle, entries: updatedEntries });
            }
          } else {
            // Update main entry
            if (runId) {
              const updatedRuns = vehicle.runs.map(run => {
                if (run.id === runId) {
                  const updatedEntries = run.entries.map(entry =>
                    entry.id === entryId ? { 
                      ...entry, 
                      contribution: contribution !== null ? contribution : entry.contribution,
                      sales_order_value: salesOrderValue !== null ? salesOrderValue : entry.sales_order_value
                    } : entry
                  );
                  return { ...run, entries: updatedEntries };
                }
                return run;
              });
              onUpdate(vehicle.id, { ...vehicle, runs: updatedRuns });
            } else {
              const updatedEntries = vehicle.entries.map((entry) =>
                entry.id === entryId ? { 
                  ...entry, 
                  contribution: contribution !== null ? contribution : entry.contribution,
                  sales_order_value: salesOrderValue !== null ? salesOrderValue : entry.sales_order_value
                } : entry
              );
              onUpdate(vehicle.id, { ...vehicle, entries: updatedEntries });
            }
          }
          console.log(`Auto-populated order ${orderNumber}: contribution = £${contribution}, sales = £${salesOrderValue}`);
        }
      } catch (error) {
        console.error('Error fetching order data:', error);
      }
    }
    // State updated - no auto-save needed, explicit saves handle persistence
  };

  const calculateSequentialMileage = async (runId = null, changedIndex = null, vehicleData = null) => {
    const basePostcode = settings?.base_postcode || 'B6 7AF';
    
    // Use explicit vehicle data if provided, otherwise use current vehicle prop
    const workingVehicle = vehicleData || vehicle;
    
    console.log(`🔵 calculateSequentialMileage called:`, {
      vehicleReg: workingVehicle.vehicle_registration,
      runId: runId || 'Run 1 (main)',
      basePostcode,
      changedIndex,
      usingExplicitData: !!vehicleData
    });
    
    try {
      // Get entries from the appropriate location using the working vehicle
      let entries;
      if (runId) {
        const run = workingVehicle.runs.find(r => r.id === runId);
        entries = run ? run.entries : [];
      } else {
        entries = workingVehicle.entries || [];
      }
      
      console.log(`📍 Found ${entries.length} entries to calculate`);
      
      // Calculate sequential distances (ORIGINAL CORRECT LOGIC RESTORED)
      const updatedEntries = await Promise.all(entries.map(async (entry, index) => {
        if (!entry.postcode || !entry.postcode.trim()) {
          console.log(`⚠️  Entry ${index}: No postcode, setting miles to 0`);
          return { ...entry, miles: 0 };
        }
        
        let fromPostcode, toPostcode;
        
        if (index === 0) {
          // First entry: Base → PC1
          fromPostcode = basePostcode;
          toPostcode = entry.postcode;
          console.log(`🚀 Entry ${index} (FIRST): ${fromPostcode} → ${toPostcode}`);
        } else {
          // Subsequent entries: Previous PC → Current PC
          const prevEntry = entries[index - 1];
          if (!prevEntry.postcode || !prevEntry.postcode.trim()) {
            // If previous entry has no postcode, calculate from base
            fromPostcode = basePostcode;
            console.log(`⚠️  Entry ${index}: Previous entry has no postcode, using base`);
          } else {
            fromPostcode = prevEntry.postcode;
          }
          toPostcode = entry.postcode;
          console.log(`📦 Entry ${index}: ${fromPostcode} → ${toPostcode}`);
        }
        
        // For last entry, also add distance back to base
        const isLastEntry = index === entries.length - 1;
        
        try {
          const distance = await calculateDistance(fromPostcode, toPostcode);
          let miles = distance || 0;
          console.log(`   ↔️  Distance: ${miles.toFixed(1)} miles`);
          
          // If this is the last entry, add return distance to base
          if (isLastEntry && entry.postcode && entry.postcode.trim()) {
            const returnDistance = await calculateDistance(entry.postcode, basePostcode);
            console.log(`   🏠 Return to base: ${returnDistance.toFixed(1)} miles`);
            miles += (returnDistance || 0);
            console.log(`   ✅ LAST ENTRY total: ${miles.toFixed(1)} miles`);
          }
          
          // ALWAYS preserve contribution from CSV - never calculate
          // Only update miles, keep contribution as-is
          console.log(`   💰 Keeping original contribution: ${entry.contribution || 0}`);
          
          return { 
            ...entry, 
            miles: parseFloat(miles.toFixed(1))
            // contribution stays unchanged from CSV
          };
        } catch (error) {
          console.error(`❌ Error calculating distance for entry ${index}:`, error);
          return { ...entry, miles: 0 };
        }
      }));
      
      console.log(`✅ Calculation complete. Updating vehicle...`);
      
      // Build the updated vehicle object
      let updatedVehicleWithMileage;
      if (runId) {
        const updatedRuns = workingVehicle.runs.map(run => {
          if (run.id === runId) {
            return { ...run, entries: updatedEntries };
          }
          return run;
        });
        updatedVehicleWithMileage = { ...workingVehicle, runs: updatedRuns };
      } else {
        updatedVehicleWithMileage = { ...workingVehicle, entries: updatedEntries };
      }
      
      // Update parent state
      onUpdate(workingVehicle.id, updatedVehicleWithMileage);
      
      // Return the updated vehicle so caller can use it for saving immediately
      return updatedVehicleWithMileage;
      
    } catch (error) {
      console.error('❌ Error calculating sequential mileage:', error);
      throw error; // Re-throw so caller knows it failed
    }
  };

  const calculateEntryMileage = async (entryId, postcode, runId = null) => {
    // Legacy function - now calls sequential calculation
    await calculateSequentialMileage(runId);
  };
  
  // Open delete confirmation modal
  const confirmDeleteEntry = (entryId, runId = null) => {
    setDeleteTarget({ type: 'entry', id: entryId, runId });
    setShowDeleteConfirm(true);
  };

  // Send a single entry back to the Unplanned Queue for this day (used by the classic
  // per-line overlay's "Unplan" button and by New UI's TransportOrderCard menu via renderEntry)
  const unplanEntry = async (entry, runId = null) => {
    try {
      await api.post(`${API}/transport-planning/unplan`, {
        date: currentDate, vehicle_id: vehicle.id, run_choice: runId || 'main', entry_id: entry.id,
        source_ui: simpleHeader ? 'new' : 'classic',
      });
      const removeFrom = (list) => (list || []).filter((e2) => e2.id !== entry.id);
      const updatedVehicle = runId
        ? { ...vehicle, runs: pruneEmptyRuns((vehicle.runs || []).map((r) => r.id === runId ? { ...r, entries: removeFrom(r.entries) } : r)) }
        : { ...vehicle, entries: removeFrom(vehicle.entries) };
      onUpdate(vehicle.id, updatedVehicle);
      if (onUnplanned) onUnplanned();
      toast.success('Sent back to Unplanned Queue');
    } catch {
      toast.error('Failed to unplan this delivery');
    }
  };
  
  // Execute delete after confirmation
  const executeDelete = () => {
    if (deleteTarget) {
      if (deleteTarget.type === 'vehicle') {
        onDelete(deleteTarget.id);
        toast.success(`Vehicle ${deleteTarget.name} deleted`);
        // Note: onDelete handles the save automatically in Dashboard
      } else if (deleteTarget.type === 'entry') {
        deleteEntry(deleteTarget.id, deleteTarget.runId);
        toast.success('Entry deleted');
      } else if (deleteTarget.type === 'run') {
        deleteRun(deleteTarget.id);
        toast.success('Run deleted');
      }
    }
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };
  
  const confirmDeleteRun = (runId) => {
    setDeleteTarget({ type: 'run', id: runId });
    setShowDeleteConfirm(true);
  };
  
  // Move Mode Functions
  const enterMoveMode = (entryId, runId) => {
    onEnterMoveMode(vehicle.id, entryId, runId || 'main');
    setOpenStatusBarId(null); // Close the menu
    // No toast - it blocks actions
  };
  
  const executeMoveEntry = (targetEntryId, targetRunId) => {
    if (!globalMoveMode && checkedEntries.length === 0) return;
    
    // If multi-selection is active, show copy/move modal
    if (checkedEntries.length > 0) {
      setPendingMoveTarget({ targetEntryId, targetRunId: targetRunId || 'main' });
      setShowCopyMoveModal(true);
      return;
    }
    
    // Single entry move
    onExecuteMove(vehicle.id, targetEntryId, targetRunId || 'main');
  };
  
  // Toggle entry selection
  const toggleEntrySelection = (entryId, runId) => {
    const sourceDate = currentDate; // Store the date this entry is from
    const key = `${vehicle.id}-${entryId}-${runId || 'main'}`;
    const isSelected = checkedEntries.some(e => `${e.vehicleId}-${e.entryId}-${e.runId}` === key);
    
    console.log(`🔘 Toggle selection: ${key} on date ${sourceDate}, currently selected: ${isSelected}`);
    
    if (isSelected) {
      const newChecked = checkedEntries.filter(e => `${e.vehicleId}-${e.entryId}-${e.runId}` !== key);
      setCheckedEntries(newChecked);
      console.log(`❌ Deselected. Now ${newChecked.length} entries selected`);
    } else {
      const newChecked = [...checkedEntries, { vehicleId: vehicle.id, entryId, runId: runId || 'main', sourceDate }];
      setCheckedEntries(newChecked);
      console.log(`✅ Selected. Now ${newChecked.length} entries selected from dates:`, newChecked.map(e => e.sourceDate));
    }
  };
  
  // Handle bulk move across vehicles - moves ALL selected entries to target location.
  // `entriesOverride` lets callers (e.g. drag-and-drop, which can't wait on the async
  // setCheckedEntries state update) move an explicit list instead of the current selection.
  const handleBulkMoveAcrossVehicles = (targetVehicleId, targetEntryId, targetRunId, entriesOverride = null) => {
    const entriesToMoveRaw = entriesOverride || checkedEntries;
    console.log(`🚚 Moving ${entriesToMoveRaw.length} entries to ${targetVehicleId}/${targetRunId}/${targetEntryId}`);

    // Quick-access Move mode can also include items picked straight from the Unplanned
    // Queue — they have no vehicle/run to remove from, so they're planned via a separate
    // API call rather than the vehicle-entry remove/insert logic below.
    if (checkedUnplannedItems.length > 0 && onMoveUnplannedToRun) {
      onMoveUnplannedToRun(checkedUnplannedItems, targetVehicleId, targetRunId);
    }

    if (entriesToMoveRaw.length === 0) return;
    
    // Save snapshot before move for undo functionality
    if (onBeforeMove) {
      onBeforeMove(allVehicles);
    }
    
    // Check if any entries are from different dates
    const sourceDates = [...new Set(entriesToMoveRaw.map(e => e.sourceDate))];
    const targetDate = currentDate;
    const hasCrossDateMove = sourceDates.some(date => date !== targetDate);
    
    if (hasCrossDateMove) {
      console.log(`⚠️ Cross-date move detected! Source dates: ${sourceDates.join(', ')}, Target: ${targetDate}`);
      if (onCrossDateMove) {
        console.log(`🌍 Calling cross-date move handler...`);
        onCrossDateMove(entriesToMoveRaw, targetVehicleId, targetEntryId, targetRunId, targetDate);
        setCheckedEntries([]); // Clear selections
        return;
      } else {
        toast.error('Cross-date moves not available');
        return;
      }
    }
    
    // Group updates by vehicle (same-date moves only)
    const vehicleUpdates = new Map();
    
    // Collect all selected entry objects and prepare for removal
    entriesToMoveRaw.forEach(({ vehicleId, entryId, runId }) => {
      console.log(`  Processing: ${vehicleId}/${runId}/${entryId}`);
      
      // Get the source vehicle data
      const sourceVehicle = allVehicles.find(v => v.id === vehicleId);
      if (!sourceVehicle) return;
      
      // Find the entry
      let entry;
      if (runId === 'main') {
        entry = sourceVehicle.entries?.find(e => e.id === entryId);
      } else {
        const run = sourceVehicle.runs?.find(r => r.id === runId);
        entry = run?.entries?.find(e => e.id === entryId);
      }
      
      if (!entry) return;
      
      // Store entry with source info
      if (!vehicleUpdates.has(vehicleId)) {
        vehicleUpdates.set(vehicleId, { toRemove: [], vehicle: sourceVehicle });
      }
      vehicleUpdates.get(vehicleId).toRemove.push({ entryId, runId, entryData: entry });
    });
    
    console.log(`📦 Collected entries from ${vehicleUpdates.size} vehicles`);
    
    // Collect all entries to move
    const entriesToMove = [];
    vehicleUpdates.forEach(({ toRemove }) => {
      toRemove.forEach(({ entryData }) => {
        entriesToMove.push(entryData);
      });
    });
    
    console.log(`✅ Total entries to move: ${entriesToMove.length}`);
    
    // Check if all moves are within the same vehicle
    const isSameVehicleMove = vehicleUpdates.size === 1 && vehicleUpdates.has(targetVehicleId);
    
    const allUpdates = [];
    
    if (isSameVehicleMove) {
      // SAME VEHICLE MOVE: Do removal and addition in one update to avoid conflicts
      console.log(`🔄 Same-vehicle move detected - handling as single update`);
      const sourceVehicle = allVehicles.find(v => v.id === targetVehicleId);
      if (sourceVehicle) {
        // Deep-enough clone (new entries array + new run objects) so this working copy
        // never aliases the original state — removal and insertion below both operate on
        // this SAME cloned object, so there's no risk of one step's array reference going
        // stale relative to the other (the root cause of an entry silently vanishing when
        // moving between two runs on the same vehicle).
        let updated = {
          ...sourceVehicle,
          entries: [...(sourceVehicle.entries || [])],
          runs: (sourceVehicle.runs || []).map(run => ({ ...run, entries: [...(run.entries || [])] })),
        };
        const { toRemove } = vehicleUpdates.get(targetVehicleId);
        
        // First, remove from source locations
        toRemove.forEach(({ entryId, runId }) => {
          if (!entryId) return; // never filter on a falsy id — would drop every entry with a missing id
          if (runId === 'main') {
            updated.entries = updated.entries.filter(e => e.id !== entryId);
          } else {
            updated.runs = updated.runs.map(run => {
              if (run.id === runId) {
                return { ...run, entries: run.entries.filter(e => e.id !== entryId) };
              }
              return run;
            });
          }
        });
        
        // Then, add to target location
        if (targetRunId === 'main') {
          if (targetEntryId === 'empty') {
            updated.entries = [...entriesToMove, ...(updated.entries || [])];
          } else {
            const entries = [...(updated.entries || [])];
            const targetIndex = entries.findIndex(e => e.id === targetEntryId);
            entries.splice(targetIndex >= 0 ? targetIndex : entries.length, 0, ...entriesToMove);
            updated.entries = entries;
          }
        } else {
          updated.runs = updated.runs.map(run => {
            if (run.id === targetRunId) {
              if (targetEntryId === 'empty') {
                return { ...run, entries: [...entriesToMove, ...(run.entries || [])] };
              } else {
                const entries = [...(run.entries || [])];
                const targetIndex = entries.findIndex(e => e.id === targetEntryId);
                entries.splice(targetIndex >= 0 ? targetIndex : entries.length, 0, ...entriesToMove);
                return { ...run, entries };
              }
            }
            return run;
          });
        }
        
        console.log(`  🔄 Updating vehicle ${targetVehicleId} with combined remove+add`);
        allUpdates.push({ id: targetVehicleId, data: updated });
        queueVehicleSave(targetVehicleId, updated);
      }
    } else {
      // CROSS-VEHICLE MOVE: Update each vehicle separately
      console.log(`🚚 Cross-vehicle move detected`);
      
      // Update each source vehicle (remove entries)
      vehicleUpdates.forEach(({ vehicle: sourceVehicle, toRemove }, vehicleId) => {
        let updated = { ...sourceVehicle, entries: [...(sourceVehicle.entries || [])], runs: (sourceVehicle.runs || []).map(run => ({ ...run, entries: [...(run.entries || [])] })) };
        
        toRemove.forEach(({ entryId, runId }) => {
          if (!entryId) return;
          if (runId === 'main') {
            updated.entries = updated.entries.filter(e => e.id !== entryId);
          } else {
            updated.runs = updated.runs.map(run => {
              if (run.id === runId) {
                return { ...run, entries: run.entries.filter(e => e.id !== entryId) };
              }
              return run;
            });
          }
        });
        
        console.log(`  📤 Removing entries from source vehicle ${vehicleId}`);
        allUpdates.push({ id: vehicleId, data: updated });
        queueVehicleSave(vehicleId, updated, { moveRole: 'remove' });
      });
      
      // Update target vehicle (add entries)
      const targetVehicle = allVehicles.find(v => v.id === targetVehicleId);
      if (targetVehicle) {
        let updatedTarget = { ...targetVehicle, entries: [...(targetVehicle.entries || [])], runs: (targetVehicle.runs || []).map(run => ({ ...run, entries: [...(run.entries || [])] })) };
        
        if (targetRunId === 'main') {
          if (targetEntryId === 'empty') {
            updatedTarget.entries = [...entriesToMove, ...(updatedTarget.entries || [])];
          } else {
            const entries = [...(updatedTarget.entries || [])];
            const targetIndex = entries.findIndex(e => e.id === targetEntryId);
            entries.splice(targetIndex >= 0 ? targetIndex : entries.length, 0, ...entriesToMove);
            updatedTarget.entries = entries;
          }
        } else {
          updatedTarget.runs = updatedTarget.runs.map(run => {
            if (run.id === targetRunId) {
              if (targetEntryId === 'empty') {
                return { ...run, entries: [...entriesToMove, ...(run.entries || [])] };
              } else {
                const entries = [...(run.entries || [])];
                const targetIndex = entries.findIndex(e => e.id === targetEntryId);
                entries.splice(targetIndex >= 0 ? targetIndex : entries.length, 0, ...entriesToMove);
                return { ...run, entries };
              }
            }
            return run;
          });
        }
        
        console.log(`  📥 Adding entries to target vehicle ${targetVehicleId}`);
        allUpdates.push({ id: targetVehicleId, data: updatedTarget });
        queueVehicleSave(targetVehicleId, updatedTarget, { moveRole: 'add' });
      }
    }
    
    // Apply all updates at once
    if (allUpdates.length > 0) {
      console.log(`💾 Applying ${allUpdates.length} vehicle updates...`);
      onUpdateMultiple(allUpdates);
    }
    
    // Clear selections
    setCheckedEntries([]);
    toast.success(`Moved ${entriesToMove.length} ${entriesToMove.length === 1 ? 'entry' : 'entries'}`);
    console.log(`✅ Move complete!`);
  };
  
  // Handle bulk move or copy
  const handleBulkMove = (isCopy) => {
    if (!pendingMoveTarget || checkedEntries.length === 0) {
      console.log('❌ handleBulkMove: Missing target or no entries selected');
      return;
    }
    
    const { targetEntryId, targetRunId } = pendingMoveTarget;
    console.log(`🎯 handleBulkMove: ${isCopy ? 'COPY' : 'MOVE'} ${checkedEntries.length} entries to ${targetRunId}/${targetEntryId}`);
    
    // Collect all selected entry objects
    const entriesToProcess = [];
    checkedEntries.forEach(({ entryId, runId }) => {
      let entry;
      if (runId === 'main') {
        entry = vehicle.entries?.find(e => e.id === entryId);
      } else {
        const run = vehicle.runs?.find(r => r.id === runId);
        entry = run?.entries?.find(e => e.id === entryId);
      }
      if (entry) {
        entriesToProcess.push({ ...entry, sourceRunId: runId });
      }
    });
    
    if (entriesToProcess.length === 0) {
      toast.error('No entries found');
      console.log('❌ No entries found to process');
      return;
    }
    
    console.log(`✅ Found ${entriesToProcess.length} entries to process`);
    
    // Create updated vehicle
    let updatedVehicle = { ...vehicle };
    
    if (isCopy) {
      // COPY: Duplicate entries to target location
      const newEntries = entriesToProcess.map(entry => ({
        ...entry,
        id: `${entry.id}-copy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Generate unique ID
      }));
      
      // Add to target location
      if (targetRunId === 'main') {
        if (targetEntryId === 'empty') {
          updatedVehicle.entries = [...newEntries, ...(updatedVehicle.entries || [])];
        } else {
          const targetIndex = updatedVehicle.entries.findIndex(e => e.id === targetEntryId);
          const entries = [...updatedVehicle.entries];
          entries.splice(targetIndex >= 0 ? targetIndex : entries.length, 0, ...newEntries);
          updatedVehicle.entries = entries;
        }
      } else {
        updatedVehicle.runs = updatedVehicle.runs.map(run => {
          if (run.id === targetRunId) {
            if (targetEntryId === 'empty') {
              return { ...run, entries: [...newEntries, ...(run.entries || [])] };
            } else {
              const targetIndex = run.entries.findIndex(e => e.id === targetEntryId);
              const entries = [...run.entries];
              entries.splice(targetIndex >= 0 ? targetIndex : entries.length, 0, ...newEntries);
              return { ...run, entries };
            }
          }
          return run;
        });
      }
      
      toast.success(`Copied ${entriesToProcess.length} ${entriesToProcess.length === 1 ? 'entry' : 'entries'}`);
    } else {
      // MOVE: Remove from source and add to target
      // First, remove from sources
      entriesToProcess.forEach(({ sourceRunId, id }) => {
        if (sourceRunId === 'main') {
          updatedVehicle.entries = updatedVehicle.entries.filter(e => e.id !== id);
        } else {
          updatedVehicle.runs = updatedVehicle.runs.map(run => {
            if (run.id === sourceRunId) {
              return { ...run, entries: run.entries.filter(e => e.id !== id) };
            }
            return run;
          });
        }
      });
      
      // Then add to target
      if (targetRunId === 'main') {
        if (targetEntryId === 'empty') {
          updatedVehicle.entries = [...entriesToProcess, ...(updatedVehicle.entries || [])];
        } else {
          const targetIndex = updatedVehicle.entries.findIndex(e => e.id === targetEntryId);
          const entries = [...updatedVehicle.entries];
          entries.splice(targetIndex >= 0 ? targetIndex : entries.length, 0, ...entriesToProcess);
          updatedVehicle.entries = entries;
        }
      } else {
        updatedVehicle.runs = updatedVehicle.runs.map(run => {
          if (run.id === targetRunId) {
            if (targetEntryId === 'empty') {
              return { ...run, entries: [...entriesToProcess, ...(run.entries || [])] };
            } else {
              const targetIndex = run.entries.findIndex(e => e.id === targetEntryId);
              const entries = [...run.entries];
              entries.splice(targetIndex >= 0 ? targetIndex : entries.length, 0, ...entriesToProcess);
              return { ...run, entries };
            }
          }
          return run;
        });
      }
      
      toast.success(`Moved ${entriesToProcess.length} ${entriesToProcess.length === 1 ? 'entry' : 'entries'}`);
    }
    
    // Update vehicle and queue save
    console.log('💾 Updating vehicle and queueing save...');
    onUpdate(vehicle.id, updatedVehicle);
    queueVehicleSave(vehicle.id, updatedVehicle);
    
    // Clear selection and reset
    setCheckedEntries([]);
    setPendingMoveTarget(null);
    
    console.log('✅ Bulk move/copy complete!');
  };


  // A run (Run 2+) that's had all its entries removed is no longer useful in the New UI's
  // Edit Mode — auto-drop it instead of requiring a manual delete. Run 1 ("main") always stays.
  const pruneEmptyRuns = (runs) => (runs || []).filter((r) => (r.entries?.length || 0) > 0);

  const deleteEntry = (entryId, runId = null) => {
    let updatedVehicle;
    if (runId) {
      const updatedRuns = pruneEmptyRuns(vehicle.runs.map(run => {
        if (run.id === runId) {
          return { ...run, entries: run.entries.filter(entry => entry.id !== entryId) };
        }
        return run;
      }));
      updatedVehicle = { ...vehicle, runs: updatedRuns };
      onUpdate(vehicle.id, updatedVehicle);
    } else {
      const updatedEntries = vehicle.entries.filter((entry) => entry.id !== entryId);
      updatedVehicle = { ...vehicle, entries: updatedEntries };
      onUpdate(vehicle.id, updatedVehicle);
    }
    
    // Queue save immediately with fresh data
    console.log(`💾 Delete Entry: Queueing save for vehicle ${vehicle.vehicle_registration}`);
    setTimeout(() => {
      queueVehicleSave(vehicle.id, updatedVehicle);
    }, 200);
  };

  const moveEntryUp = (entryId, runId = null) => {
    if (runId) {
      const updatedRuns = vehicle.runs.map(run => {
        if (run.id === runId) {
          const entries = [...run.entries];
          const index = entries.findIndex(e => e.id === entryId);
          if (index > 0) {
            [entries[index - 1], entries[index]] = [entries[index], entries[index - 1]];
          }
          return { ...run, entries };
        }
        return run;
      });
      onUpdate(vehicle.id, { ...vehicle, runs: updatedRuns });
    } else {
      const entries = [...vehicle.entries];
      const index = entries.findIndex(e => e.id === entryId);
      if (index > 0) {
        [entries[index - 1], entries[index]] = [entries[index], entries[index - 1]];
        onUpdate(vehicle.id, { ...vehicle, entries });
      }
    }
    // Removed debouncedAutoSave
  };

  const moveEntryDown = (entryId, runId = null) => {
    if (runId) {
      const updatedRuns = vehicle.runs.map(run => {
        if (run.id === runId) {
          const entries = [...run.entries];
          const index = entries.findIndex(e => e.id === entryId);
          if (index < entries.length - 1) {
            [entries[index], entries[index + 1]] = [entries[index + 1], entries[index]];
          }
          return { ...run, entries };
        }
        return run;
      });
      onUpdate(vehicle.id, { ...vehicle, runs: updatedRuns });
    } else {
      const entries = [...vehicle.entries];
      const index = entries.findIndex(e => e.id === entryId);
      if (index < entries.length - 1) {
        [entries[index], entries[index + 1]] = [entries[index + 1], entries[index]];
        onUpdate(vehicle.id, { ...vehicle, entries });
      }
    }
    // Removed debouncedAutoSave
  };

  const addSubEntry = (parentEntryId, runId = null) => {
    const newSubEntry = {
      id: `subentry_${Date.now()}`,
      order_number: '',
      weight: 0,
      sales_order_value: 0,
      contribution: 0,
    };

    if (runId) {
      const updatedRuns = vehicle.runs.map(run => {
        if (run.id === runId) {
          return {
            ...run,
            entries: run.entries.map(entry => {
              if (entry.id === parentEntryId) {
                return {
                  ...entry,
                  sub_entries: [...(entry.sub_entries || []), newSubEntry]
                };
              }
              return entry;
            })
          };
        }
        return run;
      });
      onUpdate(vehicle.id, { ...vehicle, runs: updatedRuns });
    } else {
      const updatedEntries = vehicle.entries.map(entry => {
        if (entry.id === parentEntryId) {
          return {
            ...entry,
            sub_entries: [...(entry.sub_entries || []), newSubEntry]
          };
        }
        return entry;
      });
      onUpdate(vehicle.id, { ...vehicle, entries: updatedEntries });
    }
    // Removed debouncedAutoSave
  };

  const deleteSubEntry = (parentEntryId, subEntryId, runId = null) => {
    if (runId) {
      const updatedRuns = vehicle.runs.map(run => {
        if (run.id === runId) {
          return {
            ...run,
            entries: run.entries.map(entry => {
              if (entry.id === parentEntryId) {
                return {
                  ...entry,
                  sub_entries: entry.sub_entries.filter(sub => sub.id !== subEntryId)
                };
              }
              return entry;
            })
          };
        }
        return run;
      });
      onUpdate(vehicle.id, { ...vehicle, runs: updatedRuns });
    } else {
      const updatedEntries = vehicle.entries.map(entry => {
        if (entry.id === parentEntryId) {
          return {
            ...entry,
            sub_entries: entry.sub_entries.filter(sub => sub.id !== subEntryId)
          };
        }
        return entry;
      });
      onUpdate(vehicle.id, { ...vehicle, entries: updatedEntries });
    }
    // Removed debouncedAutoSave
  };

  const updateSubEntry = (parentEntryId, subEntryId, field, value, runId = null) => {
    if (runId) {
      const updatedRuns = vehicle.runs.map(run => {
        if (run.id === runId) {
          return {
            ...run,
            entries: run.entries.map(entry => {
              if (entry.id === parentEntryId) {
                return {
                  ...entry,
                  sub_entries: entry.sub_entries.map(sub =>
                    sub.id === subEntryId ? { ...sub, [field]: value } : sub
                  )
                };
              }
              return entry;
            })
          };
        }
        return run;
      });
      onUpdate(vehicle.id, { ...vehicle, runs: updatedRuns });
    } else {
      const updatedEntries = vehicle.entries.map(entry => {
        if (entry.id === parentEntryId) {
          return {
            ...entry,
            sub_entries: entry.sub_entries.map(sub =>
              sub.id === subEntryId ? { ...sub, [field]: value } : sub
            )
          };
        }
        return entry;
      });
      onUpdate(vehicle.id, { ...vehicle, entries: updatedEntries });
    }
    // Removed debouncedAutoSave
  };

  const deleteRun = (runId) => {
    const updatedRuns = vehicle.runs.filter(run => run.id !== runId);
    const updatedVehicle = { ...vehicle, runs: updatedRuns };
    onUpdate(vehicle.id, updatedVehicle);
    
    // Queue save immediately with fresh data
    console.log(`💾 Delete Run: Queueing save for vehicle ${vehicle.vehicle_registration}`);
    setTimeout(() => {
      queueVehicleSave(vehicle.id, updatedVehicle);
    }, 200);
  };

  const calculateTotals = (entries) => {
    const totalMiles = entries.reduce((sum, entry) => sum + (parseFloat(entry.miles) || 0), 0);
    const totalWeight = entries.reduce((sum, entry) => {
      let weight = parseFloat(entry.weight) || 0;
      // Add sub-entry weights
      if (entry.sub_entries && entry.sub_entries.length > 0) {
        weight += entry.sub_entries.reduce((subSum, sub) => subSum + (parseFloat(sub.weight) || 0), 0);
      }
      return sum + weight;
    }, 0);
    const totalContribution = entries.reduce((sum, entry) => {
      let contrib = parseFloat(entry.contribution) || 0;
      // Add sub-entry contributions
      if (entry.sub_entries && entry.sub_entries.length > 0) {
        contrib += entry.sub_entries.reduce((subSum, sub) => subSum + (parseFloat(sub.contribution) || 0), 0);
      }
      return sum + contrib;
    }, 0);
    const totalSalesOrderValue = entries.reduce((sum, entry) => {
      let sales = parseFloat(entry.sales_order_value) || 0;
      // Add sub-entry sales values
      if (entry.sub_entries && entry.sub_entries.length > 0) {
        sales += entry.sub_entries.reduce((subSum, sub) => subSum + (parseFloat(sub.sales_order_value) || 0), 0);
      }
      return sum + sales;
    }, 0);
    return { totalMiles, totalWeight, totalContribution, totalSalesOrderValue };
  };

  const calculateDailyCosts = (run1Miles, additionalRunsMiles) => {
    // Get cost data from vehicle defaults or vehicle directly
    const contractHire = parseFloat(vehicle.contract_hire) || 0;
    const maintenanceInsurance = parseFloat(vehicle.maintenance_insurance) || 0;
    const moffet = parseFloat(vehicle.moffet) || 0;
    const costPerMile = parseFloat(vehicle.cost_per_mile) || 0;
    const driverCost = parseFloat(vehicle.driver_cost) || 0;
    const isOffRoad = vehicle.off_road || false;

    // Calculate days in current month
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Daily fixed cost = (hire + maint + moffet) / days in month
    // Only apply to Run 1
    const dailyFixedCost = (contractHire + maintenanceInsurance + moffet) / daysInMonth;

    // Driver cost: annual / 52 weeks / 5 days = daily cost
    // Only include if vehicle is NOT off road AND only for Run 1
    const dailyDriverCost = isOffRoad ? 0 : (driverCost / 52) / 5;

    // Mileage costs
    const run1MileageCost = costPerMile * run1Miles;
    const additionalRunsMileageCost = costPerMile * additionalRunsMiles;
    const totalMileageCost = run1MileageCost + additionalRunsMileageCost;
    
    // Total daily cost: Fixed costs + driver (Run 1 only) + all mileage costs
    const totalDailyCost = dailyFixedCost + dailyDriverCost + totalMileageCost;

    return {
      dailyFixedCost,
      dailyDriverCost,
      run1MileageCost,
      additionalRunsMileageCost,
      totalMileageCost,
      totalDailyCost
    };
  };

  const mainTotals = calculateTotals(vehicle.entries || []);
  
  // Calculate totals across all runs
  let allRunsTotalMiles = mainTotals.totalMiles;
  let allRunsTotalWeight = mainTotals.totalWeight;
  let allRunsTotalContribution = mainTotals.totalContribution;
  let allRunsTotalSalesOrderValue = mainTotals.totalSalesOrderValue;
  
  // Separate mileage for run 1 vs additional runs
  const run1Miles = mainTotals.totalMiles;
  let additionalRunsMiles = 0;
  
  if (vehicle.runs && vehicle.runs.length > 0) {
    vehicle.runs.forEach(run => {
      const runTotals = calculateTotals(run.entries || []);
      allRunsTotalMiles += runTotals.totalMiles;
      allRunsTotalWeight += runTotals.totalWeight;
      allRunsTotalContribution += runTotals.totalContribution;
      allRunsTotalSalesOrderValue += runTotals.totalSalesOrderValue;
      additionalRunsMiles += runTotals.totalMiles;
    });
  }

  const getColorClass = () => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200',
      purple: 'bg-purple-50 border-purple-200',
      green: 'bg-green-50 border-green-200',
      red: 'bg-red-50 border-red-200',
    };
    
    // Check if vehicle is overweight
    const maxWeight = vehicle.max_weight || 0;
    if (maxWeight > 0 && allRunsTotalWeight >= maxWeight) {
      return 'bg-red-50 border-red-500 border';
    }
    
    return colors[vehicle.color_code] || colors.blue;
  };

  const renderEntriesTable = (entries, runId = null, runNumber = 1) => {
    // Check if this run is full
    const isRunFull = runId 
      ? vehicle.runs?.find(r => r.id === runId)?.is_full 
      : vehicle.is_full;
    
    // Calculate totals for this run
    const runTotalMiles = entries.reduce((sum, entry) => {
      let miles = parseFloat(entry.miles) || 0;
      if (entry.sub_entries) {
        miles += entry.sub_entries.reduce((subSum, sub) => subSum + (parseFloat(sub.miles) || 0), 0);
      }
      return sum + miles;
    }, 0);
    
    const runTotalWeight = entries.reduce((sum, entry) => {
      let weight = parseFloat(entry.weight) || 0;
      if (entry.sub_entries) {
        weight += entry.sub_entries.reduce((subSum, sub) => subSum + (parseFloat(sub.weight) || 0), 0);
      }
      return sum + weight;
    }, 0);
    
    // "Add Run" is only shown after the LAST run's boxes.
    const isLastRun = runId
      ? vehicle.runs && vehicle.runs.length > 0 && vehicle.runs[vehicle.runs.length - 1].id === runId
      : !vehicle.runs || vehicle.runs.length === 0; // Run 1 (main) counts as last only if no other runs exist yet

    // Run weight colour-coding vs this vehicle's max_weight (mileage stays neutral).
    // max_weight is stored in kg in some historic vehicle records but run weights are in
    // tonnes — normalise so the ratio is always tonnes-vs-tonnes.
    const maxWeightRaw = parseFloat(vehicle.max_weight) || 0;
    const maxWeight = maxWeightRaw > 1000 ? maxWeightRaw / 1000 : maxWeightRaw;
    const weightRatio = maxWeight > 0 ? runTotalWeight / maxWeight : 0;
    // Colours for the run's weight on the white pill in the run header (red near full, amber when getting full, green otherwise).
    const runWeightColor = maxWeight === 0 ? 'text-slate-600' : weightRatio >= 0.95 ? 'text-red-600' : weightRatio >= 0.7 ? 'text-amber-600' : 'text-green-600';

    return (
      <div
        className={cardStyle ? `mb-2 rounded-2xl bg-white/5 border-2 px-1.5 pb-2.5 pt-0 ${isRunFull ? 'ring-2 ring-pink-400/60' : ''}` : `mb-4 ${isRunFull && canEdit ? 'rounded-lg p-2' : ''}`}
        style={cardStyle ? { borderColor: isRunFull ? undefined : getRunColor(runNumber) } : (isRunFull && canEdit ? { backgroundColor: '#fce7f3' } : {})}
        data-testid={cardStyle ? `run-section-${runId || 'main'}` : undefined}
      >
        {/* Run Header — New Transport UI: no boxed/coloured band anymore. Just the "Run N" pill (coloured
            to match the run's map route colour), the +Drop / Add Run pills, and the weight/mile pills,
            all sitting directly on the board. */}
        {cardStyle ? (
          <div
            className="-mx-1.5 mb-2 flex items-center justify-between gap-2 px-1 pt-1.5"
            data-testid={`run-header-${runId || 'main'}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wide text-white shadow-sm whitespace-nowrap"
                style={{ background: isRunFull ? 'linear-gradient(135deg, #fb923c, #ec4899)' : getRunColor(runNumber) }}
              >
                Run {runNumber}{isRunFull ? ' · FULL' : ''}
              </span>
              {canEdit && !isRunFull && !globalMoveMode && !(isMultiMoveMode && checkedEntries.length > 0) && (
                <RunHeaderActions
                  actions={[
                    {
                      key: 'drop',
                      label: '+Drop',
                      onClick: () => openEntryModalForNew(runId),
                      className: 'text-[11px] font-bold px-2.5 py-1 rounded-full bg-white border-2 flex items-center gap-1 shadow-sm hover:shadow-md transition-all flex-shrink-0',
                      style: { borderColor: '#ee7203', color: '#ee7203' },
                      testId: `add-entry-${vehicle.id}${runId ? `-${runId}` : ''}`,
                      dotColor: '#ee7203',
                    },
                    ...(isLastRun ? [{
                      key: 'run',
                      label: '+Run',
                      onClick: addRun,
                      className: 'text-[11px] font-bold px-2.5 py-1 rounded-full text-white flex items-center gap-1 shadow-sm hover:shadow-md hover:brightness-110 transition-all flex-shrink-0',
                      style: { backgroundColor: getRunColor(runNumber + 1), border: '2px solid #ffffff' },
                      testId: `add-run-${vehicle.id}`,
                      dotColor: getRunColor(runNumber + 1),
                    }] : []),
                    {
                      key: 'full',
                      label: 'Full',
                      onClick: () => {
                        let updatedVehicle;
                        if (runId) {
                          const updatedRuns = vehicle.runs.map((run) => (run.id === runId ? { ...run, is_full: true } : run));
                          updatedVehicle = { ...vehicle, runs: updatedRuns };
                          onUpdate(vehicle.id, updatedVehicle);
                        } else {
                          updateField('is_full', true);
                          updatedVehicle = { ...vehicle, is_full: true };
                        }
                        toast.success('Run marked as full');
                        queueVehicleSave(vehicle.id, updatedVehicle);
                      },
                      className: 'text-[11px] font-bold px-2.5 py-1 rounded-full bg-white border-2 flex items-center gap-1 shadow-sm hover:shadow-md transition-all flex-shrink-0',
                      style: { borderColor: '#ef4444', color: '#ef4444' },
                      testId: `mark-full-${vehicle.id}${runId ? `-${runId}` : ''}`,
                      dotColor: '#ef4444',
                    },
                  ]}
                />
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className={`rounded-full bg-white px-2 py-0 text-[10px] font-bold whitespace-nowrap ${runWeightColor}`}>{runTotalWeight.toFixed(2)}T</span>
              <span className="rounded-full bg-white/90 px-2 py-0 text-[10px] font-bold whitespace-nowrap text-slate-700">{runTotalMiles.toFixed(1)}mi</span>
            </div>
          </div>
        ) : (
        <div className="mb-2 rounded"
          style={isRunFull && canEdit ? {
            background: 'linear-gradient(135deg, #fb923c, #ec4899)',
            backgroundColor: '#ec4899'
          } : {
            backgroundColor: isPurple ? '#E5E7EB' : '#e6d9f5'
          }}>
          {canEdit ? (
            // Edit mode: Headers aligned with data columns - Slimmer padding
            <div className={`flex items-center gap-1.5 px-2.5 py-1 text-sm font-semibold ${isRunFull ? 'text-white' : 'text-gray-800'} relative`}>
              <div className="flex items-center justify-start w-[60px]">
                <span className="font-bold text-xs">Run {runNumber}</span>
              </div>
              <div className="flex items-center w-[70px]"></div>
              <div className="flex items-center w-[35px]"></div>{/* Invisible column for status icons */}
              <div className="flex items-center w-[165px]">Customer</div>
              <div className="text-center w-[100px]">Postcode</div>
              <div className="text-center w-[100px]">Order #</div>
              <div className="text-center w-[80px]">
                <span className="font-bold text-blue-600">{runTotalWeight.toFixed(2)}<span className="text-gray-500">T</span></span>
              </div>
              <div className="text-center w-[80px]">
                <span className="font-bold text-blue-600">{runTotalMiles.toFixed(1)}<span className="text-gray-500">MIL</span></span>
              </div>
              {/* Contribution header REMOVED per GDPR requirements */}
              {settings?.show_sales_per_line && (
                <div className="text-right min-w-[90px] mr-16">Sales</div>
              )}
              {canEdit && !isRunFull && runNumber > 1 && (
                <div className="absolute right-0.5 top-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => confirmDeleteRun(runId)}
                    className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ) : (
            // View mode: Simple header with totals
            <div className="flex items-center justify-between px-2 py-1 text-xs">
              <h4 className={`font-semibold ${isRunFull ? 'text-white' : 'text-gray-800'}`}>
                Run {runNumber}
              </h4>
              <div className="flex gap-2 text-xs">
                <span className={isRunFull ? 'text-white' : 'text-gray-700'}>
                  Miles: <span className={`font-bold ${isRunFull ? 'text-white' : 'text-blue-600'}`}>{runTotalMiles.toFixed(1)}</span>
                </span>
                <span className={isRunFull ? 'text-white' : 'text-gray-700'}>
                  T: <span className={`font-bold ${isRunFull ? 'text-white' : 'text-blue-600'}`}>{runTotalWeight.toFixed(2)}</span>
                </span>
              </div>
            </div>
          )}
        </div>
        )}
      <div className="lg:overflow-visible overflow-x-auto">
        {/* Compact Grid Layout */}
        {cardStyle && renderEntry ? (() => {
          // New UI: a "Failed" entry is pulled out of the active run flow entirely and
          // shown in its own red "Cancelled" list at the bottom of the run, so a failed
          // drop is always obviously distinguishable from the entries still being delivered.
          // Cancelled (failed) entries stay ordinary cards (the card itself shows the red border, light red background
          // and red cross) but are always listed LAST in the run, after every active entry.
          const activeEntries = [
            ...entries.filter((e) => e.status !== 'failed'),
            ...entries.filter((e) => e.status === 'failed'),
          ];
          return (
          <>
          <RunDropZone
            id={`run-${vehicle.id}-${runId || 'main'}`}
            data={{ type: 'run', vehicleId: vehicle.id, runId: runId || 'main' }}
            disabled={!(canEdit && !isViewMode)}
            className="space-y-1.5 px-1 min-h-[20px]"
            dataTestId={`run-dropzone-${vehicle.id}-${runId || 'main'}`}
          >
            <SortableContext items={activeEntries.map((e) => `entry-${e.id}`)} strategy={verticalListSortingStrategy}>
              {activeEntries.map((entry, stopIndex) => {
                const isChecked = checkedEntries.some((e) => e.vehicleId === vehicle.id && e.entryId === entry.id && e.runId === (runId || 'main'));
                const isHighlighted = !!highlightedEntryId && (
                  entry.order_number === highlightedEntryId || entry.id === highlightedEntryId ||
                  (entry.order_numbers || []).includes(highlightedEntryId)
                );
                return (
                  <div key={entry.id}>
                    {renderEntry(entry, runId, runNumber, {
                      stopNumber: stopIndex + 1,
                      canEdit, isViewMode, isMultiMoveMode, isChecked, isHighlighted,
                      onToggleCheck: () => toggleEntrySelection(entry.id, runId),
                      updateEntry, queueVehicleSave, vehicle, openEntryModalForEdit,
                      confirmDeleteEntry, unplanEntry, isEntryAutoPlanned,
                      entryServiceIssues, currentDate, checkedEntries, checkedUnplannedItems, user, dragSuppressRef,
                      handleBulkMoveAcrossVehicles,
                      openServiceIssue: (entryArg, runIdArg) => {
                        setSelectedServiceIssueEntry(entryArg);
                        setSelectedServiceIssueRunId(runIdArg);
                        const existingIssue = entryServiceIssues[entryArg.id];
                        setServiceIssueData(existingIssue
                          ? { category: existingIssue.category || '', notes: existingIssue.notes || '', id: existingIssue.id }
                          : { category: '', notes: '', id: null });
                        setShowServiceIssueModal(true);
                      },
                    })}
                  </div>
                );
              })}
            </SortableContext>
          </RunDropZone>
          {cancelledViewEntry && (
            <TransportEntryDetailModal
              isOpen={!!cancelledViewEntry}
              entry={cancelledViewEntry.entry}
              vehicle={vehicle}
              runId={cancelledViewEntry.runId}
              selectedDate={currentDate}
              user={user}
              onClose={() => setCancelledViewEntry(null)}
            />
          )}
          </>
          );
        })() : (
        <div className="space-y-2 px-1">
          {entries.map((entry, index) => {
            return (
            <div key={entry.id} className="mb-1">
              {/* Main entry row */}
              <div className="flex items-center gap-2"
              >
              {/* Glass Bar - Read-only display - SLIMMER */}
              <div className="flex-1 relative">
                {/* Status Notes Overlay - Full Bar */}
                {openStatusBarId === `${runId || 'main'}-${entry.id}` && (
                  <div className="absolute inset-0 glass-card border-2 border-orange-500 rounded-lg z-20 p-2 flex items-center justify-between shadow-2xl">
                    {/* Left side: X, Move, Note, Service with equal spacing */}
                    <div className="flex items-center gap-4">
                      {/* X to close */}
                      <button
                        onClick={() => setOpenStatusBarId(null)}
                        className="h-8 w-8 flex items-center justify-center luxury-control hover:shadow-lg flex-shrink-0"
                        title="Close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      
                      {/* Move button - Renamed from Multi-Move */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenStatusBarId(null);
                          if (typeof onEnterMultiMoveMode === 'function') {
                            onEnterMultiMoveMode();
                            toast.info('Move mode activated - Click lines to select, then use arrows to move');
                          }
                        }}
                        className="h-8 px-3 flex items-center gap-1 luxury-control hover:shadow-lg cursor-pointer flex-shrink-0 transition-all"
                        title="Enter move mode"
                      >
                        <svg className="w-4 h-4" style={{ color: '#ee7203' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                        <span className="text-xs font-semibold">Move</span>
                      </button>

                      {/* Unplan button — sends this line back to the Unplanned Queue for this day */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenStatusBarId(null);
                          unplanEntry(entry, runId);
                        }}
                        className="h-8 px-3 flex items-center gap-1 luxury-control hover:shadow-lg cursor-pointer flex-shrink-0 transition-all"
                        title="Send back to Unplanned Queue"
                        data-testid={`unplan-entry-btn-${entry.id}`}
                      >
                        <svg className="w-4 h-4" style={{ color: '#ee7203' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15L4 10l5-5M4 10h11a4 4 0 010 8h-1" />
                        </svg>
                        <span className="text-xs font-semibold">Unplan</span>
                      </button>
                      
                      {/* Combined Status Dropdown - Complete / Failed / Service Issue */}
                      <select
                        value={entry.status === 'complete' || entry.status === 'failed' || entry.status === 'service_issue' ? entry.status : ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={async (e) => {
                          const val = e.target.value;
                          if (val === 'service_issue') {
                            setSelectedServiceIssueEntry(entry);
                            setSelectedServiceIssueRunId(runId);
                            const existingIssue = entryServiceIssues[entry.id];
                            setServiceIssueData(existingIssue
                              ? { category: existingIssue.category || '', notes: existingIssue.notes || '', id: existingIssue.id }
                              : { category: '', notes: '', id: null });
                            if (openNoteDropdown) setOpenNoteDropdown(null);
                            return;
                          }
                          const updatedVehicle = await updateEntry(entry.id, 'status', val, runId);
                          queueVehicleSave(vehicle.id, updatedVehicle);
                          toast.success(val === 'complete' ? 'Marked as Complete' : val === 'failed' ? 'Marked as Failed' : 'Status cleared');
                        }}
                        className="h-8 px-2 rounded-md luxury-control text-xs font-semibold cursor-pointer flex-shrink-0 border-0"
                        title="Status"
                        data-testid={`status-select-${entry.id}`}
                      >
                        <option value="">Status: Pending</option>
                        <option value="complete">✓ Complete</option>
                        <option value="failed">✗ Failed</option>
                        <option value="service_issue">⚠ Service Issue{entryServiceIssues[entry.id] ? ' ✓' : ''}</option>
                      </select>

                      {/* Add Note Button */}
                      {canEditNotes && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTempNoteValue(entry.status_note || '');
                            setOpenNoteDropdown(openNoteDropdown === `${runId || 'main'}-${entry.id}` ? null : `${runId || 'main'}-${entry.id}`);
                            if (showServiceIssueModal && selectedServiceIssueEntry?.id === entry.id) {
                              setShowServiceIssueModal(false);
                              setSelectedServiceIssueEntry(null);
                            }
                          }}
                          className="h-8 px-3 flex items-center gap-1 luxury-control hover:shadow-lg cursor-pointer flex-shrink-0 transition-all"
                          title={entry.status_note ? "Edit note" : "Add note"}
                        >
                          <FileText className="w-4 h-4" style={{ color: '#ee7203' }} />
                          <span className="text-xs font-semibold">{entry.status_note ? 'Note' : 'Add Note'}</span>
                        </button>
                      )}
                    </div>
                    
                    {/* Right side: Edit Entry, Recheck, Delete — only visible when the 3-dot menu is open */}
                    <div className="flex items-center gap-2">
                      {/* Edit Entry icon */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenStatusBarId(null);
                          openEntryModalForEdit(entry, runId);
                        }}
                        className="h-8 w-8 flex items-center justify-center luxury-control hover:shadow-lg flex-shrink-0 transition-all"
                        title="Edit Entry"
                        data-testid={`edit-entry-icon-${entry.id}`}
                      >
                        <Edit2 className="w-4 h-4" style={{ color: '#000000' }} />
                      </button>
                      {/* Recheck icon */}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          setOpenStatusBarId(null);
                          if (!entry.order_number || entry.order_number.trim() === '') {
                            toast.error('No order number to search for');
                            return;
                          }
                          toast.info('Rechecking CSV data...');
                          try {
                            const contribResponse = await api.get(`${API}/upload/contribution-data/${entry.order_number}`);
                            const fetchedContribution = contribResponse.data.contribution || 0;
                            let fetchedSales = 0;
                            if (settings?.show_sales_per_line) {
                              const salesResponse = await api.get(`${API}/upload/sales-order-value/${entry.order_number}`);
                              fetchedSales = salesResponse.data.sales_order_value || 0;
                            }
                            let updatedVehicle = await updateEntry(entry.id, 'contribution', fetchedContribution, runId);
                            if (settings?.show_sales_per_line) {
                              updatedVehicle = await updateEntry(entry.id, 'sales_order_value', fetchedSales, runId);
                            }
                            queueVehicleSave(vehicle.id, updatedVehicle);
                            const salesText = settings?.show_sales_per_line ? `, Sales £${fetchedSales.toFixed(2)}` : '';
                            toast.success(`Updated: Contribution £${fetchedContribution.toFixed(2)}${salesText}`);
                          } catch (error) {
                            console.error('Error rechecking CSV data:', error);
                            if (error.response && error.response.status === 404) {
                              toast.error(`Order ${entry.order_number} not found in any CSV files`);
                            } else {
                              toast.error('Failed to recheck CSV data');
                            }
                          }
                        }}
                        className="h-8 w-8 flex items-center justify-center luxury-control hover:shadow-lg flex-shrink-0 transition-all"
                        title="Recheck"
                        data-testid={`recheck-icon-${entry.id}`}
                      >
                        <RefreshCw className="w-4 h-4" style={{ color: '#2563EB' }} />
                      </button>
                      {/* Delete button */}
                      <button
                        onClick={() => {
                          setOpenStatusBarId(null);
                          confirmDeleteEntry(entry.id, runId);
                        }}
                        className="h-8 w-8 flex items-center justify-center luxury-control hover:shadow-lg flex-shrink-0 transition-all"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                )}
                
                <div 
                  onClick={(e) => {
                    if (isMultiMoveMode) {
                      // Multi-move mode: toggle selection
                      toggleEntrySelection(entry.id, runId);
                    } else if (globalMoveMode) {
                      // Single move mode: move entry above clicked one
                      executeMoveEntry(entry.id, runId);
                    } else {
                      // Normal mode
                      if (isViewMode) {
                        // View mode: clicking a line with parsed order details opens the parsed order viewer
                        if (entry.embedded_order) {
                          setViewParsedOrder(entry.embedded_order);
                          setViewParsedOrderEntry(entry);
                        }
                      } else {
                        canEdit && openEntryModalForEdit(entry, runId);
                      }
                    }
                  }}
                  className={`glass-bar rounded-md p-1.5 flex items-center gap-1.5 cursor-pointer transition-all relative ${
                    entry.status === 'stock_transfer' ? '!border-2 !border-blue-500 !bg-blue-50' :
                    globalMoveMode && globalMoveMode.vehicleId === vehicle.id && globalMoveMode.entryId === entry.id && globalMoveMode.runId === (runId || 'main') ? '!bg-blue-200 !border-blue-500 !shadow-xl' :
                    isMultiMoveMode && checkedEntries.some(e => e.vehicleId === vehicle.id && e.entryId === entry.id && e.runId === (runId || 'main')) ? '!border-2 !border-green-500 !shadow-xl' :
                    isMultiMoveMode ? '!border-2 !border-blue-400 !shadow-md hover:!border-blue-600' :
                    (globalMoveMode || isMultiMoveMode) ? '!border-green-400 !shadow-lg hover:!border-green-600' :
                    entry.status === 'complete' ? '!bg-green-50 !border !border-green-400' :
                    isEntryAutoPlanned(entry) && !entry.confirmed ? 'entry-auto-planned' : ''
                  }`}
                  data-testid={`entry-row-${entry.id}`}
                  style={isMultiMoveMode ? { marginLeft: '35px' } : {}}
                >
                  {/* Green Right Arrow - Shows valid drop zone (appears on ALL UNSELECTED lines in multi-move mode) */}
                  {canEdit && !isViewMode && isMultiMoveMode && 
                   !checkedEntries.some(e => e.entryId === entry.id && e.runId === (runId || 'main') && e.vehicleId === vehicle.id) && (
                    <button
                      onClick={(e) => {
                        console.log(`🎯 Arrow clicked! Selected entries: ${checkedEntries.length}`);
                        e.stopPropagation();
                        if (checkedEntries.length > 0) {
                          console.log(`✅ Moving entries to above ${entry.id} in ${vehicle.id}/${runId || 'main'}`);
                          // Move directly - no modal
                          handleBulkMoveAcrossVehicles(vehicle.id, entry.id, runId || 'main');
                        } else {
                          console.log(`❌ No entries selected, but arrow is clickable for visual feedback`);
                          toast.info('Please select entries first');
                        }
                      }}
                      className={`absolute left-[-32px] w-[28px] h-[28px] flex items-center justify-center rounded transition-all shadow-md ${
                        checkedEntries.length > 0 ? 'bg-green-500 hover:bg-green-600 cursor-pointer' : 'bg-gray-400 cursor-pointer'
                      }`}
                      title={checkedEntries.length > 0 ? "Move selected entries above this line" : "Select entries first"}
                    >
                      <span className="text-white text-xl font-bold">→</span>
                    </button>
                  )}
                  
                  {/* 3-Dot Menu - First column - Match header width of 60px */}
                  <div className="w-[60px] flex items-center justify-start">
                    {canEdit && !isViewMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenStatusBarId(openStatusBarId === `${runId || 'main'}-${entry.id}` ? null : `${runId || 'main'}-${entry.id}`);
                        }}
                        className="h-6 w-6 flex items-center justify-center luxury-control hover:shadow-md flex-shrink-0"
                        title="Status & Actions"
                        data-testid={`status-menu-btn-${entry.id}`}
                      >
                        {entry.status_note ? (
                          <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" style={{ color: '#1d1d1b' }} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                  
                  {/* Time Display/Picker - Compact */}
                  <div className="flex items-center w-[70px]">
                    {openTimePickerId === `${runId || 'main'}-${entry.id}` ? (
                      <select
                        value={entry.time_slot || ''}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateEntry(entry.id, 'time_slot', e.target.value, runId);
                          setOpenTimePickerId(null);
                          setTimeout(() => {
                            queueVehicleSave(vehicle.id, vehicle);
                            autoSortByTime(runId);
                          }, 200);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => setOpenTimePickerId(null)}
                        disabled={!canEdit}
                        className="h-6 text-sm luxury-input font-bold shadow-sm"
                        style={{ color: '#ee7203' }}
                      >
                        <option value="">--:--</option>
                        <option value="12:00">12:00</option>
                        <option value="12:30">12:30</option>
                        <option value="1:00">1:00</option>
                        <option value="1:30">1:30</option>
                        <option value="2:00">2:00</option>
                        <option value="2:30">2:30</option>
                        <option value="3:00">3:00</option>
                        <option value="3:30">3:30</option>
                        <option value="4:00">4:00</option>
                        <option value="4:30">4:30</option>
                        <option value="5:00">5:00</option>
                        <option value="5:30">5:30</option>
                        <option value="6:00">6:00</option>
                        <option value="6:30">6:30</option>
                        <option value="7:00">7:00</option>
                        <option value="7:30">7:30</option>
                        <option value="8:00">8:00</option>
                        <option value="8:30">8:30</option>
                        <option value="9:00">9:00</option>
                        <option value="9:30">9:30</option>
                        <option value="10:00">10:00</option>
                        <option value="10:30">10:30</option>
                        <option value="11:00">11:00</option>
                        <option value="11:30">11:30</option>
                      </select>
                    ) : (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          canEdit && setOpenTimePickerId(`${runId || 'main'}-${entry.id}`);
                        }}
                        className="text-sm font-bold px-1 py-0.5 rounded cursor-pointer hover:bg-gray-100"
                        style={{ color: '#ee7203' }}
                      >
                        {entry.time_slot || '⏱️'}
                      </span>
                    )}
                  </div>
                  
                  {/* Status Icons Column - Compact invisible column */}
                  <div className="w-[35px] flex items-center justify-center">
                    {/* Status Icons - Only show if status is not pending/empty */}
                    {(() => {
                      console.log(`📊 Entry ${entry.id} status: "${entry.status}"`);
                      return null;
                    })()}
                    {/* Complete icon - green checkmark */}
                    {entry.status === 'complete' && (
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" strokeWidth={3} />
                    )}
                    {/* Failed icon - red X */}
                    {entry.status === 'failed' && (
                      <X className="w-4 h-4 text-red-600 flex-shrink-0" strokeWidth={3} />
                    )}
                    {/* Collection icon - brown package */}
                    {entry.status === 'collection' && (
                      <Package className="w-4 h-4 flex-shrink-0" style={{ color: '#D97706' }} />
                    )}
                    {/* Stock Transfer icon - blue box with arrow */}
                    {entry.status === 'stock_transfer' && (
                      <div className="w-5 h-5 bg-blue-500 rounded flex items-center justify-center flex-shrink-0" title={`Transfer to: ${BRANCHES.find(b => b.id === entry.transfer_branch_id)?.name || 'Branch'}`}>
                        <ArrowRightLeft className="w-3 h-3 text-white" />
                      </div>
                    )}
                    {/* Service Issue indicator - yellow box with white S */}
                    {entryServiceIssues[entry.id] && (
                      <div className="w-4 h-4 bg-yellow-500 rounded flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-[10px] font-bold">S</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Customer Name - Without status icons */}
                  <div className="w-[165px] min-w-0 flex items-center gap-1">
                    {entry.embedded_order && (
                      <Eye
                        className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                        data-testid={`parsed-order-indicator-${entry.id}`}
                        title="Parsed order details available — click line to view"
                      />
                    )}
                    <span 
                      className={`font-semibold text-gray-800 block leading-tight ${
                        (entry.customer_name || '').length > 32 ? 'text-[10px]' :
                        (entry.customer_name || '').length > 26 ? 'text-[11px]' :
                        (entry.customer_name || '').length > 20 ? 'text-xs' :
                        'text-sm'
                      }`}
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word'
                      }}
                    >
                      {entry.customer_name || '—'}
                    </span>
                  </div>
                  
                  {/* Postcode - evenly spaced */}
                  <div className="w-[100px] text-center">
                    <span className="text-sm font-mono font-bold" style={{ color: '#ee7203' }}>{entry.postcode || '—'}</span>
                  </div>
                  
                  {/* Order Number - evenly spaced */}
                  <div className="w-[100px] text-center">
                    <span className="text-sm font-medium text-gray-700">
                      {entry.order_number || '—'}
                    </span>
                  </div>
                  
                  {/* Weight - evenly spaced */}
                  <div className="w-[80px] text-center">
                    <span className="text-sm font-semibold text-gray-800">{entry.weight ? `${entry.weight}T` : '—'}</span>
                  </div>
                  
                  {/* Miles - evenly spaced */}
                  <div className="w-[80px] text-center flex items-center justify-center gap-1">
                    <span className="text-sm font-bold text-blue-600">{entry.miles ? `${entry.miles}mi` : '—'}</span>
                    {/* Warning icon for suspiciously high mileage (over 100 miles for single entry) */}
                    {entry.miles && entry.miles > 100 && (
                      <AlertTriangle
                        className="w-4 h-4 text-amber-500 cursor-pointer hover:text-amber-600"
                        title="Unusually high mileage - click for help"
                        onClick={() => {
                          toast.warning(
                            <div className="space-y-2">
                              <p className="font-semibold">⚠️ Unusually High Mileage Detected</p>
                              <p className="text-sm">This entry shows <strong>{entry.miles} miles</strong>, which seems unusually high for a single delivery.</p>
                              <p className="text-sm">To recalculate:</p>
                              <ol className="text-sm list-decimal list-inside ml-2">
                                <li>Click the <strong>3-dot menu (⋮)</strong> at the top right of this vehicle</li>
                                <li>Select <strong>"Recalculate Mileage"</strong></li>
                                <li>This will refresh all distance calculations</li>
                              </ol>
                            </div>,
                            { duration: 8000 }
                          );
                        }}
                      />
                    )}
                  </div>
                  
                  {/* Contribution - REMOVED per GDPR requirements */}
                  
                  {/* Sales Value (if enabled) */}
                  {settings?.show_sales_per_line && (
                    <div className="w-[90px] text-right pr-4">
                      <span className="text-sm font-semibold text-purple-600">£{(entry.sales_order_value || 0).toFixed(2)}</span>
                    </div>
                  )}

                  {/* End-of-line: Confirm tick (auto-planned + unconfirmed only) */}
                  {canEdit && !isViewMode && isEntryAutoPlanned(entry) && !entry.confirmed && (
                    <div className="flex items-center gap-1 pl-1 flex-shrink-0">
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const updatedVehicle = await updateEntry(entry.id, 'confirmed', true, runId);
                          queueVehicleSave(vehicle.id, updatedVehicle);
                          toast.success('Delivery confirmed');
                        }}
                        className="h-6 w-6 flex items-center justify-center rounded-full bg-green-500 hover:bg-green-600 shadow-md flex-shrink-0 transition-all"
                        title="Confirm this delivery"
                        data-testid={`confirm-entry-btn-${entry.id}`}
                      >
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Slide-down Note Box - Single Line with buttons on right */}
            {openNoteDropdown === `${runId || 'main'}-${entry.id}` && (
              <div 
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  maxHeight: openNoteDropdown === `${runId || 'main'}-${entry.id}` ? '60px' : '0px',
                  opacity: openNoteDropdown === `${runId || 'main'}-${entry.id}` ? 1 : 0
                }}
              >
                <div className="bg-orange-50 border-l-4 border-orange-400 rounded p-2 flex items-center gap-2 shadow-md" style={{ marginTop: '-2px' }}>
                  <input
                    type="text"
                    value={tempNoteValue}
                    onChange={(e) => setTempNoteValue(e.target.value)}
                    placeholder="Enter note..."
                    className="flex-1 h-8 border border-gray-300 rounded px-3 text-sm focus:outline-none focus:border-orange-500"
                    readOnly={!canEditNotes}
                    autoFocus
                  />
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (!canEditNotes) {
                        toast.error('You do not have permission to edit notes');
                        return;
                      }
                      const updatedVehicle = await updateEntry(entry.id, 'status_note', tempNoteValue, runId);
                      queueVehicleSave(vehicle.id, updatedVehicle);
                      setOpenNoteDropdown(null);
                      setOpenStatusBarId(null); // Close three-dots menu
                      toast.success('Note saved', { duration: 1500 });
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 h-8"
                    disabled={!canEditNotes}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setOpenNoteDropdown(null);
                      setTempNoteValue('');
                    }}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 h-8"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            {/* Slide-down Service Issue Box - Single Line with dropdown, textbox, and buttons */}
            {selectedServiceIssueEntry?.id === entry.id && selectedServiceIssueRunId === runId && (
              <div 
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  maxHeight: '60px',
                  opacity: 1
                }}
              >
                <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded p-2 flex items-center gap-2 shadow-md" style={{ marginTop: '-2px' }}>
                  <select
                    value={serviceIssueData.category}
                    onChange={(e) => setServiceIssueData({...serviceIssueData, category: e.target.value})}
                    className="h-8 border border-gray-300 rounded px-2 text-sm focus:outline-none focus:border-yellow-500"
                  >
                    <option value="">Select Issue</option>
                    <option value="Operational">Operational</option>
                    <option value="Over Committed">Over Committed</option>
                    <option value="Resource">Resource</option>
                    <option value="Input">Input</option>
                    <option value="Customer">Customer</option>
                    <option value="CLEAR">🗑️ Clear Service Issue</option>
                  </select>
                  <input
                    type="text"
                    value={serviceIssueData.notes}
                    onChange={(e) => setServiceIssueData({...serviceIssueData, notes: e.target.value})}
                    placeholder="Enter service issue notes..."
                    className="flex-1 h-8 border border-gray-300 rounded px-3 text-sm focus:outline-none focus:border-yellow-500"
                    autoFocus
                  />
                  <Button
                    size="sm"
                    onClick={async (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      
                      if (!serviceIssueData.category) {
                        toast.error('Please select an option');
                        return;
                      }
                      
                      try {
                        // Handle CLEAR option - delete the service issue
                        if (serviceIssueData.category === 'CLEAR') {
                          if (serviceIssueData.id) {
                            console.log('🗑️ Deleting service issue:', serviceIssueData.id);
                            await api.delete(`${API}/service-issues/${serviceIssueData.id}`);
                            console.log('✅ Service issue deleted');
                            if (entry.status === 'service_issue') {
                              const updatedVehicle = await updateEntry(entry.id, 'status', '', runId);
                              queueVehicleSave(vehicle.id, updatedVehicle);
                            }
                            toast.success('Service issue cleared');
                          } else {
                            toast.info('No service issue to clear');
                          }
                        } else {
                          // Normal save/update
                          console.log('💾 Saving service issue:', {
                            date: selectedDate,
                            entry_id: entry.id,
                            vehicle_id: vehicle.id,
                            category: serviceIssueData.category,
                            notes: serviceIssueData.notes,
                            id: serviceIssueData.id
                          });
                          
                          let response;
                          if (serviceIssueData.id) {
                            console.log('📝 Updating existing service issue:', serviceIssueData.id);
                            response = await api.put(`${API}/service-issues/${serviceIssueData.id}`, {
                              category: serviceIssueData.category,
                              notes: serviceIssueData.notes
                            });
                          } else {
                            console.log('➕ Creating new service issue');
                            response = await api.post(`${API}/service-issues`, {
                              date: selectedDate,
                              entry_id: entry.id,
                              vehicle_id: vehicle.id,
                              category: serviceIssueData.category,
                              notes: serviceIssueData.notes
                            });
                          }
                          
                          console.log('✅ Service issue save response:', response.data);
                          const updatedVehicle = await updateEntry(entry.id, 'status', 'service_issue', runId);
                          queueVehicleSave(vehicle.id, updatedVehicle);
                          toast.success('Service issue saved');
                        }
                        
                        // Reload service issues on edit page
                        console.log('🔄 Reloading service issues on edit page...');
                        await loadServiceIssues();
                        console.log('✅ Service issues reloaded on edit page');
                        
                        // Notify parent that service issue was saved (to refresh ViewModeGrid)
                        if (onServiceIssueSaved) {
                          console.log('📢 Notifying parent to refresh ViewModeGrid');
                          onServiceIssueSaved();
                        } else {
                          console.warn('⚠️ onServiceIssueSaved callback not provided');
                        }
                        
                        // Close the slide-down and three-dots menu
                        setSelectedServiceIssueEntry(null);
                        setSelectedServiceIssueRunId(null);
                        setServiceIssueData({ category: '', notes: '', id: null });
                        setOpenStatusBarId(null); // Close three-dots menu
                      } catch (error) {
                        console.error('❌ Error with service issue:', error);
                        console.error('Error details:', error.response?.data);
                        toast.error(`Failed: ${error.response?.data?.detail || error.message}`);
                      }
                    }}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 h-8"
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setSelectedServiceIssueEntry(null);
                      setSelectedServiceIssueRunId(null);
                      setServiceIssueData({ category: '', notes: '', id: null });
                    }}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 h-8"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            {/* Additional Order Numbers - Dropdown bars below main entry */}
            {(() => {
                console.log(`🔍 Entry ${entry.id} order_numbers:`, entry.order_numbers);
                return entry.order_numbers && entry.order_numbers.length > 0 && entry.order_numbers.map((additionalOrder, addIndex) => {
                  if (!additionalOrder || !additionalOrder.trim()) {
                    console.log(`⏭️ Skipping empty order at index ${addIndex}`);
                    return null;
                  }
                  
                  console.log(`✅ Rendering additional order ${addIndex}: ${additionalOrder}`);
                  return (
                  <div 
                    key={`${entry.id}-add-${addIndex}`}
                    className="bg-gray-100 border-l border-r border-b border-gray-300 p-1.5 flex items-center gap-1.5"
                    style={{ 
                      marginTop: addIndex === 0 ? '0' : '0',
                      marginLeft: '0',
                      borderTop: addIndex === 0 ? 'none' : '1px solid #d1d5db',
                      borderBottomLeftRadius: addIndex === entry.order_numbers.length - 1 ? '0.375rem' : '0',
                      borderBottomRightRadius: addIndex === entry.order_numbers.length - 1 ? '0.375rem' : '0'
                    }}
                  >
                    {/* Empty space to align with columns */}
                    <div className="w-[60px]"></div>
                    <div className="w-[70px]"></div>
                    <div className="w-[35px]"></div>{/* Status icons column */}
                    <div className="w-[165px]"></div>
                    <div className="w-[100px]"></div>
                    
                    {/* Order Number column */}
                    <div className="w-[100px] text-center">
                      <span className="text-sm font-medium text-gray-600">{additionalOrder}</span>
                    </div>
                    
                    {/* Weight - Empty */}
                    <div className="w-[80px]"></div>
                    
                    {/* Miles - Empty */}
                    <div className="w-[80px]"></div>
                    
                    {/* Contribution REMOVED per GDPR requirements */}
                  </div>
                  );
                });
              })()}
            </div>
          );
          })}
        </div>
        )}
      </div>

      {canEdit && !isRunFull && (
        cardStyle ? (
          <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid={`run-actions-${runId || 'main'}`}>
            {(entries.length === 0 && (globalMoveMode || (isMultiMoveMode && (checkedEntries.length > 0 || checkedUnplannedItems.length > 0)))) && (
              <button
                onClick={() => {
                  if (isMultiMoveMode && (checkedEntries.length > 0 || checkedUnplannedItems.length > 0)) {
                    handleBulkMoveAcrossVehicles(vehicle.id, 'empty', runId || 'main');
                  } else if (globalMoveMode) {
                    executeMoveEntry('empty', runId);
                  }
                }}
                className="text-xs font-bold px-3 py-1.5 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center gap-1.5 transition-all"
              >
                <MoveVertical className="w-3.5 h-3.5" /> Move Here
              </button>
            )}
          </div>
        ) : (
        <div className="mt-3 flex justify-between items-center">
          <div className="flex gap-3 items-center">
            {/* Show green Move Here button when empty run and selections exist */}
            {entries.length === 0 && (globalMoveMode || (isMultiMoveMode && checkedEntries.length > 0)) && (
              <Button
                size="sm"
                onClick={() => {
                  console.log(`🎯 Move Here button clicked! Selected entries: ${checkedEntries.length}`);
                  if (isMultiMoveMode && checkedEntries.length > 0) {
                    // Multi-move mode: move all selected entries to this empty run
                    console.log(`✅ Moving ${checkedEntries.length} entries to empty run ${runId || 'main'}`);
                    handleBulkMoveAcrossVehicles(vehicle.id, 'empty', runId || 'main');
                  } else if (globalMoveMode) {
                    // Single move mode
                    executeMoveEntry('empty', runId);
                  }
                }}
                className="gap-1 font-semibold shadow-md hover:shadow-lg transition-all bg-green-500 hover:bg-green-600 text-white border-0"
              >
                <MoveVertical className="w-4 h-4" />
                Move Here
              </Button>
            )}
            
            {/* Always show Add Entry button unless in move mode */}
            {!globalMoveMode && !(isMultiMoveMode && checkedEntries.length > 0) && (
              <Button
                size="sm"
                onClick={() => {
                  // Open modal for new entry, passing the runId so it adds to correct run
                  openEntryModalForNew(runId);
                }}
                className="gap-2 neuro-orange text-white font-semibold shadow-md hover:shadow-lg transition-all"
                data-testid={`add-entry-${vehicle.id}${runId ? `-${runId}` : ''}`}
                disabled={!canEdit}
              >
                <Plus className="w-4 h-4" />
                Add Entry
              </Button>
            )}
            
            {/* Add Run Button - Show only on the LAST run */}
            {isLastRun && (
              <Button
                size="sm"
                onClick={addRun}
                className="gap-2 neuro-button font-semibold shadow-md hover:shadow-lg transition-all"
                style={{ color: '#ee7203' }}
              >
                <Plus className="w-4 h-4" />
                Add Run
              </Button>
            )}
            
            <Button
              size="sm"
              onClick={() => {
                let updatedVehicle;
                if (runId) {
                  const updatedRuns = vehicle.runs.map(run => {
                    if (run.id === runId) {
                      toast.success('Run marked as full');
                      return { ...run, is_full: true };
                    }
                    return run;
                  });
                  updatedVehicle = { ...vehicle, runs: updatedRuns };
                  onUpdate(vehicle.id, updatedVehicle);
                } else {
                  updateField('is_full', true);
                  toast.success('Run marked as full');
                  updatedVehicle = { ...vehicle, is_full: true };
                }
                // CRITICAL FIX: Save immediately with updated data
                queueVehicleSave(vehicle.id, updatedVehicle);
              }}
              className="gap-1 neuro-button font-semibold shadow-md hover:shadow-lg transition-all"
              style={{ color: '#ef4444' }}
            >
              <Package className="w-4 h-4" />
              Mark Full
            </Button>
          </div>
          
          {/* Run P&L REMOVED per GDPR requirements */}
        </div>
        )
      )}
      
      {/* Unmark Full Button - Only show when run IS marked as full */}
      {canEdit && isRunFull && (
        cardStyle ? (
          <div className="mt-2.5 flex justify-center">
            <button
              onClick={() => {
                let updatedVehicle;
                if (runId) {
                  const updatedRuns = vehicle.runs.map(run => run.id === runId ? { ...run, is_full: false } : run);
                  updatedVehicle = { ...vehicle, runs: updatedRuns };
                  onUpdate(vehicle.id, updatedVehicle);
                } else {
                  updateField('is_full', false);
                  updatedVehicle = { ...vehicle, is_full: false };
                }
                toast.success('Run unmarked - no longer full');
                queueVehicleSave(vehicle.id, updatedVehicle);
              }}
              className="text-xs font-bold px-3 py-1.5 rounded-full bg-pink-500 hover:bg-pink-600 text-white flex items-center gap-1.5 transition-all"
            >
              <Package className="w-3.5 h-3.5" /> Unmark Full
            </button>
          </div>
        ) : (
        <div className="mt-3 flex justify-center">
          <Button
            size="sm"
            onClick={() => {
              let updatedVehicle;
              if (runId) {
                const updatedRuns = vehicle.runs.map(run => {
                  if (run.id === runId) {
                    toast.success('Run unmarked - no longer full');
                    return { ...run, is_full: false };
                  }
                  return run;
                });
                updatedVehicle = { ...vehicle, runs: updatedRuns };
                onUpdate(vehicle.id, updatedVehicle);
              } else {
                updateField('is_full', false);
                toast.success('Run unmarked - no longer full');
                updatedVehicle = { ...vehicle, is_full: false };
              }
              // Save immediately with updated data
              queueVehicleSave(vehicle.id, updatedVehicle);
            }}
            className="gap-1 font-semibold shadow-md hover:shadow-lg transition-all bg-pink-500 hover:bg-pink-600 text-white border-0"
          >
            <Package className="w-4 h-4" />
            Unmark Full
          </Button>
        </div>
        )
      )}
    </div>
  );
};

  // Check if this vehicle is not a default vehicle (needs cost button)
  const isNewVehicle = settings?.default_vehicles && 
    !settings.default_vehicles.some(dv => dv.vehicle_registration === vehicle.vehicle_registration);

  // Determine header color based on branch (E70 = purple, C30 = orange)
  const isPurple = currentBranch === 'E70';
  const headerColor = isPurple ? '#8B5CF6' : '#ee7203'; // Purple header for E70, orange for C30
  const cardBgColor = '#FFF4E6'; // Keep light orange background for all branches

  return (
    <Card className={`border-0 h-full flex flex-col ${cardStyle ? 'shadow-none bg-transparent' : 'shadow-lg'}`} style={cardStyle ? {} : {
      backgroundColor: cardBgColor, 
      borderRadius: '20px',
      overflow: 'hidden'
    }} data-testid={`vehicle-card-${vehicle.id}`}>
      {/* 3D Glass Effect Header */}
      {!simpleHeader && (
      <CardHeader className="pb-2 pt-2" style={{ 
        background: headerColor,
        borderRadius: '20px 20px 0 0',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.3), inset 0 -1px 0 rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
        border: 'none',
        position: 'relative',
        overflow: 'hidden',
        margin: 0,
        backgroundColor: headerColor
      }}>
        {/* Glass shine overlay for 3D effect */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%)',
          pointerEvents: 'none'
        }}></div>
        
        <div className="flex items-center justify-between flex-wrap gap-3 relative z-10">
          {/* Left: Vehicle Info Display (Read-only) */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h3 className="text-white text-xl font-bold tracking-wide">
              {vehicle.vehicle_registration || 'NEW VEHICLE'}
            </h3>
            <span className="px-3 py-1 rounded-full text-sm font-semibold bg-white/30 text-white backdrop-blur-sm">
              {vehicle.vehicle_type === 'truck' ? 'Truck' : 'Van'}
            </span>
            <span className="px-3 py-1 rounded-full text-sm font-semibold bg-white/30 text-white backdrop-blur-sm">
              {vehicle.driver_name || 'No Driver'}
            </span>
            {vehicle.driver_phone && (
              <span className="px-3 py-1 rounded-full text-sm font-semibold bg-white/30 text-white backdrop-blur-sm">
                {vehicle.driver_phone}
              </span>
            )}
            {vehicle.max_weight > 0 && (
              <span className="px-3 py-1 rounded-full text-sm font-semibold bg-white/30 text-white backdrop-blur-sm">
                Max: {vehicle.max_weight.toFixed(1)}T
              </span>
            )}
            {/* Moffet flag — editable per-day, defaults to the vehicle's Settings > Vehicles value */}
            {canEdit && !isViewMode ? (
              <select
                data-testid={`vehicle-moffet-select-${vehicle.id}`}
                value={(vehicle.moffet_override != null ? vehicle.moffet_override : (vehicle.has_moffet || false)) ? 'yes' : 'no'}
                onChange={async (e) => {
                  const boolVal = e.target.value === 'yes';
                  try {
                    await api.patch(`${API}/transport-planning/vehicle-moffet-override`, {
                      date: currentDate, vehicle_id: vehicle.id, has_moffet: boolVal,
                    });
                    const updatedVehicle = { ...vehicle, moffet_override: boolVal };
                    onUpdate(vehicle.id, updatedVehicle);
                    toast.success(`Moffet ${boolVal ? 'enabled' : 'disabled'} for today`);
                  } catch {
                    toast.error('Failed to update Moffet status');
                  }
                }}
                className="px-2 py-1 rounded-full text-xs font-bold bg-white/30 text-white backdrop-blur-sm border-none cursor-pointer"
                title="Has Moffet today?"
              >
                <option value="no" style={{ color: '#1d1d1b' }}>No Moffet</option>
                <option value="yes" style={{ color: '#1d1d1b' }}>🚜 Has Moffet</option>
              </select>
            ) : (vehicle.moffet_override != null ? vehicle.moffet_override : vehicle.has_moffet) && (
              <span className="px-3 py-1 rounded-full text-sm font-semibold bg-white/30 text-white backdrop-blur-sm">
                🚜 Moffet
              </span>
            )}
            {/* Saving Indicator */}
            {isSaving && (
              <div className="flex items-center gap-1 text-sm text-white font-semibold animate-pulse px-3 py-1 rounded-full bg-white/30 backdrop-blur-sm">
                <Clock className="w-4 h-4" />
                <span>Saving...</span>
              </div>
            )}
          </div>
          
          {/* Right: Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Cost Button for New Vehicles */}
            {canEdit && !isViewMode && isNewVehicle && (
              <Button
                onClick={openVehicleEditModal}
                size="sm"
                className="h-9 gap-2 neuro-orange text-white font-semibold transition-all"
                style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
              >
                <Calculator className="w-4 h-4" />
                Cost
              </Button>
            )}
            
            {/* Edit Button */}
            {canEdit && !isViewMode && (
              <Button
                onClick={openVehicleEditModal}
                size="sm"
                className="h-9 gap-2 neuro-button font-semibold transition-all"
                style={{ color: '#1d1d1b', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
              >
                <Settings className="w-4 h-4" />
                Edit
              </Button>
            )}
            
            {/* Additional Options Menu */}
            {canEdit && !isViewMode && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 p-0 neuro-button"
                    style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
                  >
                    <ChevronDownIcon className="w-4 h-4" style={{ color: '#1d1d1b' }} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border-2 border-gray-200 shadow-lg">
                  <DropdownMenuItem 
                    onClick={() => {
                      const newOffRoadStatus = !vehicle.off_road;
                      const updatedVehicle = { ...vehicle, off_road: newOffRoadStatus };
                      onUpdate(vehicle.id, updatedVehicle);
                      
                      // Save immediately
                      queueVehicleSave(vehicle.id, updatedVehicle);
                      
                      toast.success(newOffRoadStatus ? 'Vehicle marked as off road and saved' : 'Vehicle back on road and saved');
                    }}
                    className="cursor-pointer hover:bg-gray-100 py-2"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    {vehicle.off_road ? 'Back On Road' : 'Mark Off Road'}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={async () => {
                      clearGeocodeCache();
                      toast.info('Cache cleared. Recalculating all mileage...');
                      setCalculatingMileage(true);
                      
                      try {
                        // Recalculate mileage for main entries
                        const updatedVehicle = await calculateSequentialMileage(null);
                        
                        // Recalculate mileage for all runs
                        let finalVehicle = updatedVehicle;
                        if (vehicle.runs && vehicle.runs.length > 0) {
                          for (const run of vehicle.runs) {
                            finalVehicle = await calculateSequentialMileage(run.id);
                          }
                        }
                        
                        // CRITICAL FIX: Save the recalculated mileage to database
                        queueVehicleSave(vehicle.id, finalVehicle);
                        
                        toast.success('✅ Mileage recalculated and saved!');
                      } catch (error) {
                        console.error('Error recalculating mileage:', error);
                        toast.error('Failed to recalculate mileage. Check console for details.');
                      } finally {
                        setCalculatingMileage(false);
                      }
                    }}
                    className="cursor-pointer hover:bg-gray-100 py-2"
                    disabled={calculatingMileage}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${calculatingMileage ? 'animate-spin' : ''}`} />
                    {calculatingMileage ? 'Calculating...' : 'Recalculate All Mileage'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      if (typeof onEnterMultiMoveMode === 'function') {
                        onEnterMultiMoveMode();
                      }
                    }}
                    className="cursor-pointer hover:bg-gray-100 py-2"
                  >
                    <MoveVertical className="w-4 h-4 mr-2" />
                    Move Mode
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>
      )}
      <CardContent className={cardStyle ? `flex-1 p-0 ${vehicle.off_road ? 'opacity-50' : ''}` : `flex-1 ${vehicle.off_road ? 'opacity-50' : ''}`}>
        {/* Main Run - Show off-road bar if vehicle is off road */}
        {vehicle.off_road ? (
          <div className="mb-4">
            <div className="bg-white border-2 border-red-500 rounded-md p-3 flex items-center justify-center gap-2">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <span className="font-bold text-xl text-red-600">VEHICLE OFF ROAD</span>
            </div>
          </div>
        ) : (
          renderEntriesTable(vehicle.entries || [], null, 1)
        )}

        {/* Additional Runs */}
        {vehicle.runs && vehicle.runs.map((run, index) => (
          <div key={run.id}>
            {renderEntriesTable(run.entries || [], run.id, run.run_number || index + 2)}
          </div>
        ))}

        {/* Totals with Cost Information - Single Line - Right Aligned. New UI removes this
            entirely — only the per-run totals (shown in each run's header pill above) remain. */}
        {!cardStyle && (
        <div className="flex items-center justify-end pt-3 border-t-2 border-gray-400">
          <div className="flex gap-4 text-xs font-semibold flex-wrap items-center justify-end" data-testid={`totals-${vehicle.id}`}>
            {allRunsTotalWeight >= (vehicle.max_weight || 0) && vehicle.max_weight > 0 && (
              <span className="text-xs font-semibold animate-pulse text-red-600">⚠️ Max capacity</span>
            )}
            <span className="text-gray-700">Miles: <span className="text-sm font-bold text-blue-600">{allRunsTotalMiles.toFixed(1)}</span></span>
            <span className="text-gray-700">Weight: <span className={allRunsTotalWeight >= (vehicle.max_weight || 0) && vehicle.max_weight > 0 ? "font-bold text-sm text-red-600" : "text-sm font-bold text-blue-600"}>{allRunsTotalWeight.toFixed(2)}T</span></span>
            {/* Sales, Contribution, Daily Cost and P&L REMOVED per GDPR requirements */}
          </div>
        </div>
        )}
        
        {/* Cost Breakdown Modal */}
        <Dialog open={showCostBreakdown} onOpenChange={setShowCostBreakdown}>
          <DialogContent className="max-w-4xl w-[95vw] md:w-[85vw] lg:max-w-3xl bg-white max-h-[80vh] overflow-y-auto" zIndex={10002}>
            <DialogHeader>
              <DialogTitle>Cost Breakdown - {vehicle.vehicle_registration}</DialogTitle>
              <DialogDescription>View detailed cost breakdown for this vehicle</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 bg-white">
              {(() => {
                const costs = calculateDailyCosts(run1Miles, additionalRunsMiles);
                const mainTotals = calculateTotals(vehicle.entries || []);
                
                return (
                  <>
                    {/* If vehicle is off road, only show Grand Total */}
                    {vehicle.off_road ? (
                      <div className="border-2 rounded-lg p-4 bg-gray-100 border-gray-400">
                        <div className="flex justify-between font-bold text-lg">
                          <span>Total Daily Cost:</span>
                          <span className="text-red-600">£{costs.totalDailyCost.toFixed(2)}</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Run 1 Costs */}
                        <div className="border rounded-lg p-4 bg-gray-50">
                          <h3 className="font-bold text-lg mb-3 text-purple-700">Run 1</h3>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Contract Hire:</span>
                              <span className="font-semibold">£{costs.dailyFixedCost.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Maintenance:</span>
                              <span className="font-semibold">£{((vehicle.maintenance_insurance || 0) / 22).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Moffet:</span>
                              <span className="font-semibold">£{((vehicle.moffet || 0) / 22).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Driver:</span>
                              <span className="font-semibold">£{costs.dailyDriverCost.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Fuel ({run1Miles.toFixed(1)} mi):</span>
                              <span className="font-semibold">£{costs.run1MileageCost.toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="flex justify-between font-bold border-t mt-3 pt-3">
                            <span>Run 1 Total:</span>
                            <span className="text-red-600">£{(costs.dailyFixedCost + costs.dailyDriverCost + costs.run1MileageCost).toFixed(2)}</span>
                          </div>
                        </div>
                        
                        {/* Additional Runs Costs */}
                        {additionalRunsMiles > 0 && vehicle.runs && vehicle.runs.map((run, idx) => {
                          const runTotals = calculateTotals(run.entries || []);
                          const runMileageCost = runTotals.totalMiles * (vehicle.cost_per_mile || 0);
                          
                          return (
                            <div key={idx} className="border rounded-lg p-4 bg-blue-50">
                              <h3 className="font-bold text-lg mb-3 text-blue-700">Run {idx + 2}</h3>
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Fuel ({runTotals.totalMiles.toFixed(1)} mi):</span>
                                  <span className="font-semibold">£{runMileageCost.toFixed(2)}</span>
                                </div>
                              </div>
                              <div className="flex justify-between font-bold border-t mt-3 pt-3">
                                <span>Run {idx + 2} Total:</span>
                                <span className="text-red-600">£{runMileageCost.toFixed(2)}</span>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Grand Total */}
                        <div className="border-2 rounded-lg p-4 bg-gray-100 border-gray-400">
                          <div className="flex justify-between font-bold text-lg">
                            <span>Grand Total Daily Cost:</span>
                            <span className="text-red-600">£{costs.totalDailyCost.toFixed(2)}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>

        {/* Entry Edit Modal */}
        <Dialog open={showEntryEditModal} onOpenChange={(open) => {
          if (!open) {
            setShowEntryEditModal(false);
            setEditingEntry(null);
            setEditingRunId(null);
            setParsedOrder(null);
            setShowParsedOrderPanel(false);
            setEntryModalData({ customer_name:'', postcode:'', order_number:'', order_numbers:[], time_slot:'', weight:0, miles:0, contribution:0, sales_order_value:0, status:'pending', status_note:'', linked_transfers:[] });
          }
        }}>
          <DialogContent
            hideCloseButton={true}
            className="bg-white p-0"
            style={{
              top: '4%',
              transform: 'translateX(-50%)',
              maxHeight: '92vh',
              zIndex: 10002,
              /* width expands smoothly when parsed panel opens */
              width: showParsedOrderPanel ? 'min(98vw, 1200px)' : 'min(95vw, 600px)',
              maxWidth: showParsedOrderPanel ? 'min(98vw, 1200px)' : 'min(95vw, 600px)',
              transition: 'width 320ms cubic-bezier(0.4,0,0.2,1), max-width 320ms cubic-bezier(0.4,0,0.2,1)',
              border: '2px solid #ee7203',
              borderRadius: '16px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(238,114,3,0.15), 0 8px 32px rgba(0,0,0,0.12)',
            }}
            zIndex={10002}
          >
            <div className="flex flex-1 overflow-hidden" style={{ minHeight: 0 }}>

              {/* ── Left: Edit Form ─────────────────────────────── */}
              <div className="flex flex-col overflow-y-auto flex-shrink-0" style={{ width: '100%', minWidth: showParsedOrderPanel ? '520px' : 'unset', maxWidth: showParsedOrderPanel ? '520px' : 'unset' }}>

                {/* Header */}
                <div className="px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <span className="font-bold text-gray-800 text-base">{editingEntry ? 'Edit Entry' : 'Add New Entry'}</span>
                    <div className="flex items-center gap-2">
                      {entryModalData.linked_transfers && entryModalData.linked_transfers.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTimeout(() => setShowTransferDetails(true), 0); }}
                          className="text-xs font-semibold px-3 py-1.5 rounded-full bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1.5 transition-colors"
                          data-testid="show-transfer-details-btn"
                        >
                          <FileText className="w-3.5 h-3.5" /> Transfer Details
                        </button>
                      )}
                      {!editingEntry && (
                        <button
                          type="button"
                          onClick={() => { setTransferRefInputs(['']); setShowCollectTransferModal(true); }}
                          className="text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5 transition-colors"
                          data-testid="collect-transfer-btn"
                        >
                          <Package className="w-3.5 h-3.5" /> Collect Transfer
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowEntryEditModal(false);
                          setParsedOrder(null);
                          setShowParsedOrderPanel(false);
                        }}
                        className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Order Number — top centre */}
                  <div className="flex flex-col items-center gap-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Order Number</Label>
                    <div className="flex items-center gap-2 w-full max-w-xs">
                      <input
                        id="modal-order-number"
                        value={entryModalData.order_number}
                        onChange={(e) => setEntryModalData({...entryModalData, order_number: e.target.value})}
                        onBlur={async (e) => {
                          const orderNumber = e.target.value.trim();
                          if (orderNumber) {
                            checkCancelledTicket(orderNumber);
                            try {
                              const contributionResponse = await api.get(`${API}/upload/contribution-data/${orderNumber}`);
                              let fetchedContribution = entryModalData.contribution || 0;
                              if (contributionResponse.data.contribution !== null) fetchedContribution = contributionResponse.data.contribution;
                              const salesOrderResponse = await api.get(`${API}/upload/sales-order-value/${orderNumber}`);
                              let fetchedSalesOrderValue = entryModalData.sales_order_value || 0;
                              if (salesOrderResponse.data.sales_order_value !== null) fetchedSalesOrderValue = salesOrderResponse.data.sales_order_value;
                              setEntryModalData({ ...entryModalData, contribution: fetchedContribution, sales_order_value: fetchedSalesOrderValue });
                              toast.success(`Contribution: £${fetchedContribution.toFixed(2)}`);
                            } catch { /* no csv data */ }
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleOrderLookup(); }}
                        placeholder="e.g. 3471577"
                        className="flex-1 h-11 px-4 text-center text-lg font-bold rounded-full border-2 border-gray-200 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
                        style={{ letterSpacing: '0.05em' }}
                        data-testid="modal-order-number"
                      />
                      <button
                        type="button"
                        onClick={handleOrderLookup}
                        disabled={lookingUpOrder}
                        className="h-11 px-4 rounded-full text-white font-semibold text-sm flex items-center gap-1.5 transition-all hover:opacity-90 disabled:opacity-60 flex-shrink-0"
                        style={{ backgroundColor: '#ee7203' }}
                        data-testid="order-lookup-btn"
                        title="Look up order from uploaded PDFs"
                      >
                        {lookingUpOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                        Update
                      </button>
                    </div>

                    {/* Sender badge — shown when order sender is resolved */}
                    {orderSender && (
                      <div className="flex items-center gap-1.5 mt-1" data-testid="order-sender-badge">
                        <span className="text-xs text-gray-400">Order from</span>
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
                          style={{ backgroundColor: '#fff7ed', color: '#c2410c' }}>
                          {orderSender}
                        </span>
                      </div>
                    )}

                    {/* Additional order numbers */}
                    {entryModalData.order_numbers && entryModalData.order_numbers.map((orderNum, idx) => (
                      <div key={idx} className="flex items-center gap-2 w-full max-w-xs">
                        <Input
                          value={orderNum}
                          onChange={(e) => {
                            const newOrderNumbers = [...entryModalData.order_numbers];
                            newOrderNumbers[idx] = e.target.value;
                            setEntryModalData({...entryModalData, order_numbers: newOrderNumbers});
                          }}
                          placeholder={`Order number ${idx + 2}`}
                          className="flex-1 text-center font-semibold rounded-full"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newOrderNumbers = entryModalData.order_numbers.filter((_, i) => i !== idx);
                            setEntryModalData({...entryModalData, order_numbers: newOrderNumbers});
                          }}
                          className="h-8 w-8 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200 transition-all"
                        >
                          <X className="w-3.5 h-3.5 text-red-600" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        if (entryModalData.status === 'stock_transfer' && (entryModalData.linked_transfers?.length || 0) > 0) {
                          const existing = (entryModalData.linked_transfers || []).map((t) => (t.transfer_ref || '').toUpperCase()).filter(Boolean);
                          setTransferRefInputs([...existing, '']);
                          setShowCollectTransferModal(true);
                        } else if (entryModalData.order_number.trim()) {
                          setEntryModalData({ ...entryModalData, order_numbers: [...(entryModalData.order_numbers || []), ''] });
                        }
                      }}
                      className="text-xs text-orange-600 hover:text-orange-700 font-medium underline"
                      data-testid="entry-plus-btn"
                    >
                      + Add another order number
                    </button>
                  </div>
                </div>

                {/* Form fields */}
                <div className="px-5 py-4 space-y-4 flex-1">
                  {/* Customer Name */}
                  <div>
                    <Label htmlFor="modal-customer-name" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer Name</Label>
                    <Input
                      id="modal-customer-name"
                      value={entryModalData.customer_name}
                      onChange={(e) => setEntryModalData({...entryModalData, customer_name: e.target.value.toUpperCase()})}
                      placeholder="Customer name"
                      className="mt-1 rounded-full"
                      data-testid="modal-customer-name"
                    />
                  </div>

                  {/* Postcode + Weight side by side */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="modal-postcode" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Postcode *</Label>
                      <Input
                        id="modal-postcode"
                        value={entryModalData.postcode}
                        onChange={(e) => setEntryModalData({...entryModalData, postcode: e.target.value.toUpperCase()})}
                        placeholder="e.g. DY12 2UX"
                        className="mt-1 rounded-full"
                        data-testid="modal-postcode"
                      />
                    </div>
                    <div>
                      <Label htmlFor="modal-weight" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Weight (Tonnes)</Label>
                      <Input
                        id="modal-weight"
                        type="text"
                        inputMode="decimal"
                        value={entryModalData.weight === 0 ? '0' : entryModalData.weight || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) setEntryModalData({...entryModalData, weight: value === '' ? '' : value});
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value !== '') setEntryModalData({...entryModalData, weight: parseFloat(value) || 0});
                        }}
                        placeholder="0.000"
                        className="mt-1 rounded-full"
                        data-testid="modal-weight"
                      />
                    </div>
                  </div>

                  {/* Time Slot + Status */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="modal-time-slot" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Time Slot</Label>
                      <Input
                        id="modal-time-slot"
                        type="text"
                        value={entryModalData.time_slot || ''}
                        onChange={(e) => setEntryModalData({...entryModalData, time_slot: e.target.value})}
                        placeholder="e.g. 12:00"
                        className="mt-1 rounded-full"
                      />
                    </div>
                    <div>
                      <Label htmlFor="modal-status" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</Label>
                      <select
                        id="modal-status"
                        value={entryModalData.status || ''}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          if (newStatus === 'stock_transfer') {
                            setShowBranchSelect(true);
                          } else {
                            setEntryModalData({...entryModalData, status: newStatus, transfer_branch_id: null});
                          }
                        }}
                        className="mt-1 w-full h-9 px-4 rounded-full border border-gray-300 text-sm"
                      >
                        <option value="">No status</option>
                        <option value="completed">✓ Complete</option>
                        <option value="failed">✗ Failed</option>
                        <option value="collection">📦 Collect</option>
                        <option value="stock_transfer">📦➡️ Stock Transfer</option>
                      </select>
                      {entryModalData.status === 'stock_transfer' && entryModalData.transfer_branch_id && (
                        <div className="mt-1 p-2 bg-blue-50 border border-blue-200 rounded-xl">
                          <p className="text-xs text-blue-700">
                            Transfer to: <strong>{BRANCHES.find(b => b.id === entryModalData.transfer_branch_id)?.name}</strong>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {settings?.show_sales_per_line && (
                    <div>
                      <Label htmlFor="modal-sales" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sales Value (£)</Label>
                      <Input
                        id="modal-sales"
                        type="number"
                        step="0.01"
                        value={entryModalData.sales_order_value}
                        onChange={(e) => setEntryModalData({...entryModalData, sales_order_value: parseFloat(e.target.value) || 0})}
                        placeholder="0.00"
                        className="mt-1 rounded-full"
                      />
                    </div>
                  )}

                  {/* View Order button — standalone, full width */}
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (entryModalData.status === 'stock_transfer') {
                          if ((entryModalData.linked_transfers?.length || 0) > 0) setShowTransferDetails(true);
                          else toast.info('Link a transfer first using "Collect Transfer"');
                          return;
                        }
                        if (parsedOrder) {
                          setShowParsedOrderPanel(p => !p);
                        } else if ((entryModalData.order_number || '').trim()) {
                          handleOrderLookup();
                        } else {
                          toast.info('Enter an order number and click Update first');
                        }
                      }}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border-2 text-sm font-semibold transition-all hover:shadow-md"
                      style={{
                        borderColor: (parsedOrder || entryModalData.status === 'stock_transfer') ? '#ee7203' : '#d1d5db',
                        color: (parsedOrder || entryModalData.status === 'stock_transfer') ? '#ee7203' : '#6b7280',
                        background: (parsedOrder || entryModalData.status === 'stock_transfer') ? '#fff8f0' : '#fafafa',
                      }}
                      data-testid="view-order-btn"
                    >
                      {lookingUpOrder ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : entryModalData.status === 'stock_transfer' ? (
                        <Package className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                      {entryModalData.status === 'stock_transfer'
                        ? 'View Transfer'
                        : (showParsedOrderPanel ? 'Hide Order View' : (parsedOrder ? 'View Parsed Order' : 'View Order'))}
                    </button>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 flex-shrink-0 bg-gray-50" style={{ borderBottomLeftRadius: '14px', borderBottomRightRadius: showParsedOrderPanel ? 0 : '14px' }}>
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={() => {
                      setShowEntryEditModal(false);
                      setEditingEntry(null);
                      setEditingRunId(null);
                      setParsedOrder(null);
                      setShowParsedOrderPanel(false);
                      setEntryModalData({ customer_name:'', postcode:'', order_number:'', order_numbers:[], time_slot:'', weight:0, miles:0, contribution:0, sales_order_value:0, status:'pending', status_note:'', linked_transfers:[] });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveEntryFromModal}
                    className="rounded-full"
                    style={{ backgroundColor: '#ee7203', color: 'white' }}
                    data-testid="save-entry-btn"
                  >
                    {editingEntry ? 'Update Entry' : 'Add Entry'}
                  </Button>
                </div>
              </div>

              {/* ── Right: Parsed Order View — slides in ──────────── */}
              <div
                style={{
                  overflow: 'hidden',
                  transition: 'max-width 320ms cubic-bezier(0.4,0,0.2,1), opacity 280ms ease',
                  maxWidth: showParsedOrderPanel && parsedOrder ? '680px' : '0px',
                  opacity: showParsedOrderPanel && parsedOrder ? 1 : 0,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {parsedOrder && (
                  <div className="flex flex-col h-full" style={{ width: '680px', borderLeft: '1px solid #f5e6d3' }}>
                    <div className="flex items-center justify-between px-4 py-2.5 border-b flex-shrink-0" style={{ borderBottomColor: '#f5e6d3', background: '#fff8f0' }}>
                      <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#ee7203' }}>Parsed Order View</span>
                      <button onClick={() => setShowParsedOrderPanel(false)} className="p-1 rounded-full hover:bg-orange-100 text-orange-400">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      <ParsedOrderViewModal order={parsedOrder} inline={true} />
                    </div>
                  </div>
                )}
              </div>

            </div>
          </DialogContent>
        </Dialog>

        {/* Collect Transfer Sub-Modal */}
        <Dialog open={showCollectTransferModal} onOpenChange={setShowCollectTransferModal}>
          <DialogContent className="max-w-md bg-white" zIndex={10002}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-700">
                <Package className="w-5 h-5" />
                {entryModalData.status === 'stock_transfer' && (entryModalData.linked_transfers?.length || 0) > 0
                  ? `Add Transfers from ${
                      entryModalData.linked_transfers[0].collection_branch_name ||
                      entryModalData.linked_transfers[0].branch_name ||
                      'the same branch'
                    }`
                  : 'Collect Stock Transfer'}
              </DialogTitle>
              <DialogDescription>
                {entryModalData.status === 'stock_transfer' && (entryModalData.linked_transfers?.length || 0) > 0
                  ? 'Existing transfers on this entry are pre-filled below. Add more references from the same branch, or click the search icon to browse.'
                  : 'Enter one or more transfer references (e.g. C30-26-0001). All transfers must be from the same branch.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              {transferRefInputs.map((ref, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    autoFocus={idx === transferRefInputs.length - 1}
                    value={ref}
                    onChange={(e) => {
                      const copy = [...transferRefInputs];
                      copy[idx] = e.target.value.toUpperCase();
                      setTransferRefInputs(copy);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (ref.trim()) setTransferRefInputs([...transferRefInputs, '']);
                      }
                    }}
                    placeholder={idx === 0 ? 'C30-26-0001' : 'Add new number'}
                    className="flex-1 font-mono"
                    data-testid={`transfer-ref-input-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => openTransferPicker(idx)}
                    title="Browse recent transfers"
                    className="flex-shrink-0 p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
                    data-testid={`browse-transfer-${idx}`}
                  >
                    <Search className="w-4 h-4" />
                  </button>
                  {transferRefInputs.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setTransferRefInputs(transferRefInputs.filter((_, i) => i !== idx))}
                      className="text-gray-400 hover:text-red-500 p-1"
                      data-testid={`remove-transfer-ref-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setTransferRefInputs([...transferRefInputs, ''])}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                data-testid="add-transfer-ref-line"
              >
                <Plus className="w-3.5 h-3.5" />Add another transfer
              </button>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="outline" onClick={() => setShowCollectTransferModal(false)} data-testid="collect-transfer-cancel">
                Cancel
              </Button>
              <Button
                disabled={loadingTransfers}
                onClick={async () => {
                  const refs = transferRefInputs.map(r => (r || '').trim().toUpperCase()).filter(Boolean);
                  if (!refs.length) { toast.error('Enter at least one transfer reference'); return; }
                  setLoadingTransfers(true);
                  try {
                    const fetched = [];
                    for (const ref of refs) {
                      const res = await fetch(`${API}/stock-transfers/by-ref/${encodeURIComponent(ref)}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                      });
                      if (!res.ok) {
                        const e = await res.json();
                        throw new Error(`${ref}: ${e.detail || 'not found'}`);
                      }
                      const data = await res.json();
                      fetched.push(data.transfer);
                    }
                    const originBranches = [...new Set(fetched.map(t => t.branch_id))];
                    if (originBranches.length > 1) {
                      throw new Error(`All transfers must be from the same branch. Found: ${originBranches.join(', ')}`);
                    }
                    const collectingBranchCode = fetched[0].branch_id;
                    // Fetch branch record for display name + postcode
                    let branchDisplay = fetched[0].branch_name || `${collectingBranchCode} Warehouse`;
                    let branchPostcode = '';
                    try {
                      const bRes = await fetch(`${API}/branches/${encodeURIComponent(collectingBranchCode)}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                      });
                      if (bRes.ok) {
                        const bData = await bRes.json();
                        const branch = bData.branch || bData;
                        branchDisplay = branch.display_name || branch.name || branchDisplay;
                        branchPostcode = branch.postcode || branch.address_postcode || '';
                      }
                    } catch (_) { /* ignore */ }
                    // Fallback: parse postcode from the transfer's stored branch_address string
                    if (!branchPostcode && fetched[0].branch_address) {
                      const parts = String(fetched[0].branch_address).split(',').map(s => s.trim()).filter(Boolean);
                      if (parts.length) branchPostcode = parts[parts.length - 1];
                    }
                    // Fallback postcode map for branches where the DB doesn't store a postcode yet
                    if (!branchPostcode) {
                      const FALLBACK_POSTCODES = { 'C30': 'B9 4DD', 'E70': 'B70 0TX' };
                      branchPostcode = FALLBACK_POSTCODES[collectingBranchCode] || '';
                    }
                    const totalWeight = fetched.reduce((sum, t) => sum + (parseFloat(t.total_weight) || 0), 0);
                    setEntryModalData((prev) => ({
                      ...prev,
                      customer_name: branchDisplay,
                      postcode: branchPostcode,
                      order_number: refs[0],
                      order_numbers: refs.slice(1),
                      status: 'stock_transfer',
                      weight: totalWeight,
                      linked_transfers: fetched.map(t => ({
                        id: t.id,
                        transfer_ref: t.transfer_ref,
                        // The branch we COLLECT FROM (selected branch on the transfer)
                        collection_branch_id: t.branch_id,
                        collection_branch_name: t.branch_name,
                        collection_branch_address: t.branch_address,
                        collection_branch_contact: t.branch_contact,
                        collection_branch_phone: t.branch_phone,
                        // Keep legacy fields for backwards compatibility
                        origin_branch_id: t.origin_branch_id,
                        branch_id: t.branch_id,
                        branch_name: t.branch_name,
                        requested_by: t.requested_by,
                        collection_date: t.collection_date,
                        items: t.items || [],
                        total_weight: t.total_weight || 0,
                      })),
                    }));
                    toast.success(`Loaded ${fetched.length} transfer(s) from ${branchDisplay}`);
                    setShowCollectTransferModal(false);
                    // Never show a stale/coincidental "parsed sales order" for a transfer entry
                    setParsedOrder(null);
                    setShowParsedOrderPanel(false);
                    // Fire-and-forget: notify configured recipients that a branch-to-branch
                    // stock transfer has been arranged (Settings > Notifications). Never
                    // blocks the UI if it fails/isn't configured.
                    api.post(`${API}/transport-planning/stock-transfer-notify`, {
                      collection_branch_name: branchDisplay,
                      collection_branch_postcode: branchPostcode,
                      transfer_refs: refs,
                      delivery_date: currentDate || selectedDate,
                      run_slot: editingRunId ? 'PM' : 'AM',
                      vehicle_name: vehicle.vehicle_registration || vehicle.name || '',
                    }).catch(() => {});
                  } catch (e) {
                    toast.error(e.message);
                  } finally {
                    setLoadingTransfers(false);
                  }
                }}
                style={{ backgroundColor: '#2563eb', color: 'white' }}
                data-testid="collect-transfer-save"
              >
                {loadingTransfers ? 'Loading…' : 'Save & Populate Entry'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Duplicate-order detection — this order is already elsewhere on Transport */}
        <Dialog open={!!duplicateOrderInfo} onOpenChange={(open) => { if (!open) cancelDuplicateOrder(); }}>
          <DialogContent className="max-w-md bg-white" zIndex={10005} data-testid="duplicate-order-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Order Already on Transport
              </DialogTitle>
            </DialogHeader>
            {duplicateOrderInfo && (
              <div className="space-y-3 pt-1">
                <p className="text-sm text-gray-700">
                  Order <span className="font-mono font-bold">#{duplicateOrderInfo.order_number}</span> for{' '}
                  <span className="font-bold">{duplicateOrderInfo.customer_name || 'this customer'}</span> is already{' '}
                  {duplicateOrderInfo.location === 'board' ? (
                    <>planned on <span className="font-bold">{formatDupDate(duplicateOrderInfo.date)}</span> ({duplicateOrderInfo.run_label}) on vehicle{' '}
                      <span className="font-bold">{duplicateOrderInfo.vehicle_name}</span></>
                  ) : (
                    <>sitting in the Unplanned Queue for <span className="font-bold">{formatDupDate(duplicateOrderInfo.date)}</span></>
                  )}.
                </p>
                <p className="text-sm text-gray-600">
                  Move it here instead? This will remove it from where it currently is, then add it to this location.
                </p>
                <div className="flex gap-2 justify-end pt-2">
                  <Button
                    variant="outline"
                    onClick={cancelDuplicateOrder}
                    data-testid="duplicate-order-cancel-btn"
                  >
                    Cancel — Leave It There
                  </Button>
                  <Button
                    onClick={confirmMoveDuplicateHere}
                    style={{ backgroundColor: '#ee7203', color: 'white' }}
                    data-testid="duplicate-order-move-btn"
                  >
                    Move Here
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Transfer Picker Modal — browse recent transfers */}
        <Dialog open={showTransferPicker} onOpenChange={setShowTransferPicker}>
          <DialogContent className="max-w-4xl max-h-[85vh] bg-white" zIndex={10002}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-700">
                <Package className="w-5 h-5" />
                {entryModalData.status === 'stock_transfer' && (entryModalData.linked_transfers?.length || 0) > 0
                  ? `Recent Transfers from ${
                      entryModalData.linked_transfers[0].collection_branch_name ||
                      entryModalData.linked_transfers[0].branch_name ||
                      'the same branch'
                    }`
                  : 'Recent Transfers'}
              </DialogTitle>
              <DialogDescription>
                Click a transfer to add its reference to the current line.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[65vh] overflow-y-auto pr-1">
              {transferPickerLoading && (
                <div className="text-center py-12 text-gray-500 text-sm">Loading transfers…</div>
              )}
              {!transferPickerLoading && transferPickerList.length === 0 && (
                <div className="text-center py-12 text-gray-500 text-sm">No recent transfers</div>
              )}
              {!transferPickerLoading && transferPickerList.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {transferPickerList.map((t) => {
                    const created = t.created_at ? new Date(t.created_at) : null;
                    const day = created ? String(created.getDate()).padStart(2, '0') : '';
                    const month = created ? created.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase() : '';
                    const weekday = created ? created.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase() : '';
                    const firstItemName = t.items?.[0]?.name || t.items?.[0]?.description || '';
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          const copy = [...transferRefInputs];
                          copy[transferPickerTargetIdx] = t.transfer_ref || '';
                          setTransferRefInputs(copy);
                          setShowTransferPicker(false);
                        }}
                        className="text-left border-2 border-blue-100 hover:border-blue-500 rounded-2xl p-4 transition-all bg-white hover:shadow-lg hover:-translate-y-0.5"
                        data-testid={`transfer-picker-item-${t.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-bold text-base text-gray-900 truncate flex-1">{t.branch_name}</h3>
                          <div className="flex flex-col items-center text-gray-600 flex-shrink-0">
                            <span className="text-[10px] font-bold tracking-wider">{weekday}</span>
                            <span className="text-xl font-bold leading-none">{day}</span>
                            <span className="text-[10px] font-bold tracking-wider">{month}</span>
                          </div>
                        </div>
                        {t.transfer_ref && (
                          <p className="text-xs font-mono font-bold text-blue-600 tracking-wide mb-1">
                            {t.transfer_ref}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mb-1.5">{t.items?.length || 0} items</p>
                        {firstItemName && (
                          <p className="text-xs text-gray-500 truncate mb-2" title={firstItemName}>{firstItemName}</p>
                        )}
                        {t.requested_by && (
                          <p className="text-xs text-gray-400">By: {t.requested_by}</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Transfer Details Modal — shown when clicking "Show Transfer Details" inside Entry Modal */}
        <Dialog open={showTransferDetails} onOpenChange={setShowTransferDetails}>
          <DialogContent className="max-w-3xl w-[95vw] bg-white max-h-[85vh] overflow-y-auto" zIndex={10002}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-purple-700">
                <FileText className="w-5 h-5" />
                Transfer Details
              </DialogTitle>
              <DialogDescription>
                Full collection address and list of items being collected on this entry.
              </DialogDescription>
            </DialogHeader>
            {(entryModalData.linked_transfers || []).length === 0 ? (
              <div className="py-8 text-center text-gray-500 text-sm">
                No linked transfers on this entry.
              </div>
            ) : (
              <div className="space-y-4 py-2">
                {/* Collection Address Card */}
                <div className="rounded-xl border-2 border-purple-200 bg-purple-50 p-4">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-purple-600 mb-1">
                    Collection From
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    {entryModalData.linked_transfers[0].collection_branch_name
                      || entryModalData.linked_transfers[0].branch_name
                      || transferDetailsBranch?.name
                      || transferDetailsBranch?.display_name
                      || (transferDetailsBranch?.branchCode ? `${transferDetailsBranch.branchCode} Warehouse` : entryModalData.customer_name)}
                  </div>
                  <div className="text-sm text-gray-700 mt-1 whitespace-pre-line">
                    {entryModalData.linked_transfers[0].collection_branch_address
                      || [
                          transferDetailsBranch?.address1,
                          transferDetailsBranch?.address2,
                          transferDetailsBranch?.city,
                          transferDetailsBranch?.postcode,
                        ]
                          .filter(Boolean)
                          .join(', ')
                      || entryModalData.postcode
                      || 'Address not available'}
                  </div>
                  {(entryModalData.linked_transfers[0].collection_branch_contact || transferDetailsBranch?.default_contact || transferDetailsBranch?.contact) && (
                    <div className="text-xs text-gray-600 mt-2">
                      <span className="font-semibold">Contact:</span>{' '}
                      {entryModalData.linked_transfers[0].collection_branch_contact || transferDetailsBranch?.default_contact || transferDetailsBranch?.contact}
                    </div>
                  )}
                  {(entryModalData.linked_transfers[0].collection_branch_phone || transferDetailsBranch?.phone) && (
                    <div className="text-xs text-gray-600">
                      <span className="font-semibold">Phone:</span>{' '}
                      {entryModalData.linked_transfers[0].collection_branch_phone || transferDetailsBranch?.phone}
                    </div>
                  )}
                </div>

                {/* Transfer refs + items */}
                {entryModalData.linked_transfers.map((t, tIdx) => (
                  <div key={t.id || tIdx} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-200">
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-blue-700 text-sm" data-testid={`transfer-details-ref-${tIdx}`}>
                          {t.transfer_ref || 'N/A'}
                        </span>
                        {t.requested_by && (
                          <span className="text-xs text-gray-500">by {t.requested_by}</span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {t.items?.length || 0} item{(t.items?.length || 0) === 1 ? '' : 's'}
                        {t.total_weight ? ` · ${Number(t.total_weight).toFixed(1)} kg` : ''}
                      </span>
                    </div>
                    {(t.items || []).length === 0 ? (
                      <div className="px-4 py-3 text-xs text-gray-500">No items on this transfer.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100 text-gray-700">
                            <tr>
                              <th className="text-left px-3 py-2 font-semibold">Code</th>
                              <th className="text-left px-3 py-2 font-semibold">Description</th>
                              <th className="text-right px-3 py-2 font-semibold">Qty</th>
                              <th className="text-left px-3 py-2 font-semibold">Unit</th>
                              <th className="text-right px-3 py-2 font-semibold">Weight</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(t.items || []).map((it, iIdx) => (
                              <tr key={iIdx} className="border-t border-gray-100" data-testid={`transfer-details-item-${tIdx}-${iIdx}`}>
                                <td className="px-3 py-2 font-mono text-xs text-gray-800">{it.product_code || '-'}</td>
                                <td className="px-3 py-2 text-gray-800">{it.description || '-'}</td>
                                <td className="px-3 py-2 text-right font-semibold">{it.quantity_required ?? it.quantity ?? 0}</td>
                                <td className="px-3 py-2 text-gray-600">{it.stock_unit || '-'}</td>
                                <td className="px-3 py-2 text-right text-gray-700">
                                  {it.line_weight ? `${Number(it.line_weight).toFixed(1)} kg` : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end pt-3 border-t mt-2">
              <Button
                variant="outline"
                onClick={() => setShowTransferDetails(false)}
                data-testid="transfer-details-close"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Branch Selection Dialog for Stock Transfer */}
        <Dialog open={showBranchSelect} onOpenChange={setShowBranchSelect}>
          <DialogContent className="max-w-lg bg-white" zIndex={10002}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-600">
                <ArrowRightLeft className="w-5 h-5" />
                Select Transfer Branch
              </DialogTitle>
              <DialogDescription>
                Choose the branch for this stock transfer
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[400px] overflow-y-auto space-y-2 py-4">
              {BRANCHES.map(branch => (
                <button
                  key={branch.id}
                  onClick={() => {
                    setEntryModalData({
                      ...entryModalData,
                      status: 'stock_transfer',
                      transfer_branch_id: branch.id,
                      customer_name: branch.name,
                      postcode: branch.postcode
                    });
                    setShowBranchSelect(false);
                  }}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all"
                >
                  <div className="font-semibold text-gray-900">{branch.name}</div>
                  <div className="text-sm text-gray-500">{branch.city} • {branch.postcode}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowBranchSelect(false)}>
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Vehicle Edit Modal */}
        <Dialog open={showVehicleEditModal} onOpenChange={setShowVehicleEditModal}>
          <DialogContent className="max-w-4xl w-[95vw] md:w-[85vw] lg:max-w-3xl bg-white border-2 border-orange-300 rounded-2xl shadow-2xl max-h-[80vh] overflow-y-auto" zIndex={10002}>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold flex items-center gap-2" style={{ color: '#ee7203' }}>
                <Settings className="w-6 h-6" />
                Edit Vehicle Details
              </DialogTitle>
              <DialogDescription className="text-base pt-2 text-gray-600">
                Update vehicle registration, driver information, and costs
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label htmlFor="vehicle-reg">Vehicle Registration *</Label>
                <Input
                  id="vehicle-reg"
                  value={vehicleEditData.vehicle_registration}
                  onChange={(e) => setVehicleEditData({...vehicleEditData, vehicle_registration: e.target.value.toUpperCase()})}
                  placeholder="e.g. VX24 YXK"
                  className="mt-1 font-bold"
                />
              </div>
              
              <div>
                <Label htmlFor="vehicle-type">Vehicle Type</Label>
                <Select
                  value={vehicleEditData.vehicle_type}
                  onValueChange={(value) => setVehicleEditData({...vehicleEditData, vehicle_type: value})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="truck">🚛 Truck</SelectItem>
                    <SelectItem value="van">🚐 Van</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="driver-name">Driver Name</Label>
                <Input
                  id="driver-name"
                  value={vehicleEditData.driver_name}
                  onChange={(e) => setVehicleEditData({...vehicleEditData, driver_name: e.target.value})}
                  placeholder="Enter driver name"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="driver-phone">Driver Phone</Label>
                <Input
                  id="driver-phone"
                  value={vehicleEditData.driver_phone}
                  onChange={(e) => setVehicleEditData({...vehicleEditData, driver_phone: e.target.value})}
                  placeholder="e.g. 07970569490"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="max-weight">Max Weight (Tonnes)</Label>
                <Input
                  id="max-weight"
                  type="number"
                  step="0.1"
                  value={vehicleEditData.max_weight}
                  onChange={(e) => setVehicleEditData({...vehicleEditData, max_weight: parseFloat(e.target.value) || 0})}
                  placeholder="0.0"
                  className="mt-1"
                />
              </div>
              
              {/* Show cost fields for new vehicles */}
              {isNewVehicle && (
                <>
                  <div>
                    <Label htmlFor="cost-per-mile">Cost Per Mile (£)</Label>
                    <Input
                      id="cost-per-mile"
                      type="number"
                      step="0.01"
                      value={vehicleEditData.cost_per_mile}
                      onChange={(e) => setVehicleEditData({...vehicleEditData, cost_per_mile: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="driver-cost">Driver Daily Cost (£)</Label>
                    <Input
                      id="driver-cost"
                      type="number"
                      step="0.01"
                      value={vehicleEditData.driver_cost}
                      onChange={(e) => setVehicleEditData({...vehicleEditData, driver_cost: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                      className="mt-1"
                    />
                  </div>
                </>
              )}
            </div>
            
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              {/* Left: Delete Vehicle Button */}
              <Button
                variant="outline"
                onClick={() => {
                  setShowVehicleEditModal(false);
                  setDeleteTarget({ type: 'vehicle', id: vehicle.id, name: vehicle.vehicle_registration });
                  setShowDeleteConfirm(true);
                }}
                className="bg-red-50 text-red-600 hover:bg-red-100 border-red-300 font-semibold px-6"
              >
                Delete Vehicle
              </Button>
              
              {/* Right: Cancel & Save */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowVehicleEditModal(false)}
                  className="neuro-button px-6"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveVehicleFromModal}
                  className="neuro-orange text-white font-semibold px-6"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Delete Confirmation Modal */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="max-w-md bg-white border-2 border-red-300 rounded-2xl shadow-2xl" zIndex={10002}>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-red-600 flex items-center gap-2">
                <AlertCircle className="w-6 h-6" />
                Confirm Delete
              </DialogTitle>
              <DialogDescription className="text-base pt-2 text-gray-600">
                {deleteTarget?.type === 'vehicle'
                  ? `Are you sure you want to delete vehicle ${deleteTarget.name}? All entries and runs for this vehicle will be permanently removed from this day.`
                  : deleteTarget?.type === 'run' 
                  ? 'Are you sure you want to delete this entire run? All entries in this run will be permanently removed.'
                  : 'Are you sure you want to delete this entry? This action cannot be undone.'
                }
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                className="neuro-button px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={executeDelete}
                className="text-white font-semibold px-6 shadow-lg"
                style={{ background: 'linear-gradient(145deg, #ef4444, #dc2626)' }}
              >
                Delete {deleteTarget?.type === 'vehicle' ? 'Vehicle' : deleteTarget?.type === 'run' ? 'Run' : 'Entry'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      </CardContent>
      
      {/* Service Issue Modal */}
      <Dialog open={showServiceIssueModal} onOpenChange={setShowServiceIssueModal}>
        <DialogContent className="max-w-md bg-white rounded-2xl shadow-2xl border-2 border-yellow-400" zIndex={10002}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-yellow-600 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Service Issue
            </DialogTitle>
            <DialogDescription className="text-base pt-2 text-gray-600">
              {serviceIssueData.id ? 'Update the service issue details' : 'Report a service issue for this delivery'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* Customer Info */}
            {selectedServiceIssueEntry && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <div className="font-semibold text-gray-700">{selectedServiceIssueEntry.customer_name}</div>
                <div className="text-gray-600">{selectedServiceIssueEntry.order_number} • {selectedServiceIssueEntry.postcode}</div>
              </div>
            )}
            
            {/* Category Dropdown */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                Category <span className="text-red-500">*</span>
              </Label>
              <select
                value={serviceIssueData.category}
                onChange={(e) => setServiceIssueData({ ...serviceIssueData, category: e.target.value })}
                className="w-full p-2.5 border-2 border-yellow-400 rounded-lg text-sm bg-white focus:border-yellow-500 focus:outline-none"
              >
                <option value="">Select category...</option>
                <option value="Over Committed">Over Committed</option>
                <option value="Operational">Operational</option>
                <option value="Resource">Resource</option>
              </select>
            </div>
            
            {/* Notes */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                Notes
              </Label>
              <textarea
                value={serviceIssueData.notes}
                onChange={(e) => setServiceIssueData({ ...serviceIssueData, notes: e.target.value })}
                placeholder="Describe the service issue..."
                className="w-full p-2.5 border-2 border-yellow-400 rounded-lg resize-none text-sm focus:border-yellow-500 focus:outline-none"
                rows={3}
              />
            </div>
          </div>
          
          <div className="flex justify-between gap-3 pt-6 border-t border-gray-200">
            {serviceIssueData.id && (
              <Button
                variant="destructive"
                onClick={async () => {
                  if (window.confirm('Are you sure you want to clear this service issue?')) {
                    try {
                      await api.delete(`${API}/service-issues/${serviceIssueData.id}`);
                      toast.success('Service issue cleared!');
                      setShowServiceIssueModal(false);
                      setServiceIssueData({ category: '', notes: '', id: null });
                      
                      // Reload service issues
                      const response = await api.get(`${API}/service-issues`);
                      const issues = response.data.service_issues || [];
                      const issuesMap = {};
                      issues.forEach(issue => {
                        issuesMap[issue.entry_id] = issue;
                      });
                      setEntryServiceIssues(issuesMap);
                    } catch (error) {
                      console.error('Error deleting service issue:', error);
                      toast.error('Failed to clear service issue');
                    }
                  }
                }}
                className="px-6"
              >
                Clear Issue
              </Button>
            )}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowServiceIssueModal(false)}
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                if (!serviceIssueData.category) {
                  toast.error('Please select a category');
                  return;
                }
                
                if (!selectedServiceIssueEntry) {
                  toast.error('No entry selected');
                  return;
                }
                
                try {
                  // Use the current date from parent component
                  const dateStr = currentDate ? format(currentDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
                  
                  const issuePayload = {
                    entry_id: selectedServiceIssueEntry.id,
                    date: dateStr,
                    vehicle_registration: vehicle.vehicle_registration,
                    customer_name: selectedServiceIssueEntry.customer_name || '',
                    order_number: selectedServiceIssueEntry.order_number || '',
                    postcode: selectedServiceIssueEntry.postcode || '',
                    miles: selectedServiceIssueEntry.miles || 0,
                    contribution: selectedServiceIssueEntry.contribution || 0,
                    category: serviceIssueData.category,
                    notes: serviceIssueData.notes
                  };
                  
                  console.log('💾 Saving service issue:', issuePayload);
                  
                  if (serviceIssueData.id) {
                    // Update existing
                    await api.put(`${API}/service-issues/${serviceIssueData.id}`, issuePayload);
                    toast.success('Service issue updated!');
                  } else {
                    // Create new
                    await api.post(`${API}/service-issues`, issuePayload);
                    toast.success('Service issue created!');
                  }
                  
                  setShowServiceIssueModal(false);
                  
                  // Reload service issues
                  const response = await api.get(`${API}/service-issues`);
                  const issues = response.data.service_issues || [];
                  const issuesMap = {};
                  issues.forEach(issue => {
                    issuesMap[issue.entry_id] = issue;
                  });
                  setEntryServiceIssues(issuesMap);
                  
                } catch (error) {
                  console.error('❌ Error saving service issue:', error);
                  console.error('Error response:', error.response?.data);
                  console.error('Error status:', error.response?.status);
                  toast.error(`Failed to save service issue: ${error.response?.data?.detail || error.message}`);
                }
              }}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6"
            >
              {serviceIssueData.id ? 'Update Issue' : 'Save Issue'}
            </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        @keyframes slideInHorizontal {
          from {
            transform: scaleX(0);
            transform-origin: left;
          }
          to {
            transform: scaleX(1);
            transform-origin: left;
          }
        }
        .animate-slide-in-horizontal {
          animation: slideInHorizontal 0.5s ease-out forwards;
        }
      `}</style>
      
      {/* Copy/Move Modal - Appears when moving selected entries */}
      <Dialog open={showCopyMoveModal} onOpenChange={setShowCopyMoveModal}>
        <DialogContent className="max-w-sm bg-white rounded-3xl shadow-2xl border-2 border-orange-500" zIndex={10002}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center" style={{ color: '#ee7203' }}>
              Move or Copy?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-center text-gray-700 mb-6">
              You have {checkedEntries.length} {checkedEntries.length === 1 ? 'entry' : 'entries'} selected.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  handleBulkMove(false); // false = move (not copy)
                  setShowCopyMoveModal(false);
                }}
                className="w-full py-6 text-lg font-bold text-white rounded-2xl shadow-lg hover:shadow-xl transition-all"
                style={{ backgroundColor: '#ee7203' }}
              >
                🚚 Move
                <span className="block text-sm font-normal mt-1">Remove from source</span>
              </Button>
              <Button
                onClick={() => {
                  handleBulkMove(true); // true = copy
                  setShowCopyMoveModal(false);
                }}
                className="w-full py-6 text-lg font-bold text-white rounded-2xl shadow-lg hover:shadow-xl transition-all bg-blue-600 hover:bg-blue-700"
              >
                📋 Copy
                <span className="block text-sm font-normal mt-1">Keep original and duplicate</span>
              </Button>
              <Button
                onClick={() => setShowCopyMoveModal(false)}
                variant="outline"
                className="w-full py-3 rounded-2xl"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Standalone Parsed Order Viewer (view mode line click) */}
      {viewParsedOrder && (
        <ParsedOrderViewModal
          order={viewParsedOrder}
          onClose={() => { setViewParsedOrder(null); setViewParsedOrderEntry(null); }}
          allOrderNumbers={[viewParsedOrderEntry?.order_number, ...(viewParsedOrderEntry?.order_numbers || [])].filter((n) => n && String(n).trim())}
          navLoading={viewParsedOrderLoading}
          onNavigateOrder={(num) => {
            setViewParsedOrderLoading(true);
            api.get(`${API}/automations/orders/${num}`)
              .then((res) => setViewParsedOrder(res.data))
              .catch(() => toast.error(`Could not load order #${num}`))
              .finally(() => setViewParsedOrderLoading(false));
          }}
        />
      )}
    </Card>
  );
};

export default VehicleCard;
